import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, ElementRef, HostListener, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { DeepDiveSaveDialogComponent } from './deep-dive-save-dialog/deep-dive-save-dialog.component';
import { ConfigService } from '../../shared-services/config.service';
import { SimulationService } from '../../shared-services/simulation.service';
import { formatDisplayValueByContext } from '../../utils/format.utils';

@Component({
  selector: 'app-deep-dive',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatSelectModule, MatIconModule, FormsModule, MatDialogModule, MatFormFieldModule],
  templateUrl: './deep-dive.component.html',
  styleUrl: './deep-dive.component.scss'
})
export class DeepDiveComponent implements OnInit, OnDestroy {
  private readonly APPLIED_FILTER_TRACE_KEY = '[APPLIED_FILTER_TRACE]';
  private readonly TOP_FILTER_DEBUG_KEY = '[TOP_FILTER_DEBUG]';
  @Input() data: any;
  @ViewChild('reportTableScroller') reportTableScroller?: ElementRef<HTMLDivElement>;
  @ViewChildren('reportHeaderCell') reportHeaderCells?: QueryList<ElementRef<HTMLTableCellElement>>;

  loaded = false;
  activeTab: 'column-selection' | 'saved-selection' | 'report-view' = 'column-selection';

  // API Configuration based on current entity (e.g. view_pas_oem_region)
  // These will be driven by the dashboard JSON if provided, otherwise default.
  entityName = 'view_pas_oem_region';
  columnEntity = 'view_pas_all_region';
  filterEntity = 'dn_pricing';
  filterNameValue = 'dn_values';
  workflowId = 'ABC-123';
  batchId = 'test';
  savedFilterEntity = 'deep_dive_saved_filters';
  savedFilterCreatedBy = '';
  savedFilterFunctionName = 'GetSavedDeepDive';
  loadPayloadEntity = 'simulation';
  loadRequestEntity = 'dn_pricing';
  loadFunctionName = 'DnOemOutboundFilter';
  exportPayloadEntity = '';
  exportRequestEntity = '';
  exportFunctionName = 'OutboundFileAllRegion';
  exportColumnsKey = '';
  loadOffset = 0;
  loadLimit = 20;
  prefillAppliedFiltersFromDetails = true;
  loadAllTopFiltersOnInit = false;
  exportSuccessMessage = 'Export completed successfully. Returning to Column Selection.';
  exportSuccessTab: 'column-selection' | 'saved-selection' | 'report-view' = 'column-selection';

  // State for Top Black Filter Bar
  topFilters: any[] = [
    { label: 'Pricing Action', key: 'pricing_action', options: [] as any[], optionsMap: {} },
    { label: 'Region', key: 'region', options: [] as any[], optionsMap: {} },
    { label: 'Product BU', key: 'product_bu', options: [] as any[], optionsMap: {} },
    { label: 'Business Owner', key: 'business_owner', options: [] as any[], optionsMap: {} },
    { label: 'RPF Flag', key: 'regional_pricing_flag', options: [] as any[], optionsMap: {} },
    { label: 'Brand', key: 'brand', options: [] as any[], optionsMap: {} },
    { label: 'Part Number', key: 'part_number', options: [] as any[], optionsMap: {} }
  ];
  selectedTopFilters: { [key: string]: any } = {};

  // State for Column Selection Tab
  availableFields: any[] = [];
  selectedFieldToAdd: any = null;
  fieldSearchText = '';
  selectedColumns: any[] = []; // Currently configuring columns
  isColumnEditMode = false;

  // State for Saved Selection Tab
  savedReports: any[] = [];
  selectedReport: any = null;
  private latestSavedFilterRows: any[] = [];

  // State for Report View Tab
  activeReportColumns: any[] = [];
  reportData: any[] = [];
  readonly pageSize = 50;
  currentPage = 1;
  totalRecords = 0;

  private subscriptions: Subscription = new Subscription();
  private simulationFilterContext: { part_number_list: string[]; region_list: string[] } = {
    part_number_list: [],
    region_list: []
  };
  private deepDiveAppliedFilters: Array<{
    field: string;
    operator: string;
    value: string | string[];
    logicalOperator?: '' | 'AND' | 'OR';
    beginScope?: string;
    endScope?: string;
  }> = [];
  private appliedTopFilterOperators: { [key: string]: string } = {};
  private selectedSimulationEntity = '';
  private hasLoadedTopFilters = false;
  private pendingTopFilterPrefill: { [key: string]: any } = {};
  private lockedTopFilterValues: { [key: string]: any[] } = {};
  private userModifiedTopFilters: { [key: string]: boolean } = {};

  // Grand total summary row
  summaryData: any = null;
  isReportLoadInProgress = false;
  isExportInProgress = false;
  exportPopupVisible = false;
  exportPopupMessage = '';
  headerColumnWidths: number[] = [];
  reportTableScrollLeft = 0;
  reportTableContentWidth = 0;
  private simulationName = '';
  private simulationUuid = '';
  simulationResults: any[] | null = null;
  private lastLoadedDeepDiveColumns: string[] = [];
  private isEditingExistingReport = false;
  private hasHierarchicalGrouping = false;
  private pendingTopFilterReload: ReturnType<typeof setTimeout> | null = null;
  private pendingTopFilterSelectionRefresh: ReturnType<typeof setTimeout> | null = null;
  private pendingGrandTotalLayoutSync: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private simulationService: SimulationService,
    private keycloakService: KeycloakService
  ) { }

  ngOnInit() {
    // Read config from dashboard JSON if provided
    if (this.data) {
      if (this.data.entity) this.entityName = this.data.entity;
      if (this.data.columnEntity) this.columnEntity = this.data.columnEntity;
      if (this.data.filterEntity) this.filterEntity = this.data.filterEntity;
      if (this.data.filterName) this.filterNameValue = this.normalizeTopFilterName(this.data.filterName);
      if (this.data.savedFilterEntity) {
        this.savedFilterEntity = String(this.data.savedFilterEntity).trim() || this.savedFilterEntity;
      }
      if (this.data.savedFilterCreatedBy) {
        this.savedFilterCreatedBy = String(this.data.savedFilterCreatedBy).trim();
      }
      if (this.data.savedFilterFunctionName) {
        this.savedFilterFunctionName = String(this.data.savedFilterFunctionName).trim() || this.savedFilterFunctionName;
      }
      if (this.data.loadPayloadEntity) {
        this.loadPayloadEntity = String(this.data.loadPayloadEntity).trim() || this.loadPayloadEntity;
      }
      if (this.data.loadRequestEntity) {
        this.loadRequestEntity = String(this.data.loadRequestEntity).trim() || this.loadRequestEntity;
      }
      if (this.data.loadFunctionName) {
        this.loadFunctionName = String(this.data.loadFunctionName).trim() || this.loadFunctionName;
      }
      if (this.data.exportPayloadEntity) {
        this.exportPayloadEntity = String(this.data.exportPayloadEntity).trim();
      }
      if (this.data.exportRequestEntity) {
        this.exportRequestEntity = String(this.data.exportRequestEntity).trim();
      }
      if (this.data.exportFunctionName) {
        this.exportFunctionName = String(this.data.exportFunctionName).trim() || this.exportFunctionName;
      }
      if (this.data.exportColumnsKey) {
        this.exportColumnsKey = String(this.data.exportColumnsKey).trim();
      }
      if (this.data.loadOffset !== undefined && this.data.loadOffset !== null && !Number.isNaN(Number(this.data.loadOffset))) {
        this.loadOffset = Number(this.data.loadOffset);
      }
      if (this.data.loadLimit !== undefined && this.data.loadLimit !== null && !Number.isNaN(Number(this.data.loadLimit))) {
        this.loadLimit = Number(this.data.loadLimit);
      }
      if (this.data.prefillAppliedFiltersFromDetails !== undefined) {
        this.prefillAppliedFiltersFromDetails = this.data.prefillAppliedFiltersFromDetails !== false;
      }
      if (this.data.loadAllTopFiltersOnInit !== undefined) {
        this.loadAllTopFiltersOnInit = this.data.loadAllTopFiltersOnInit === true;
      }
      if (this.data.exportSuccessMessage !== undefined) {
        const configuredMessage = String(this.data.exportSuccessMessage || '').trim();
        if (configuredMessage) {
          this.exportSuccessMessage = configuredMessage;
        }
      }
      if (this.data.exportSuccessTab !== undefined) {
        const configuredTab = String(this.data.exportSuccessTab || '').trim() as 'column-selection' | 'saved-selection' | 'report-view';
        const allowedTabs = new Set(['column-selection', 'saved-selection', 'report-view']);
        if (allowedTabs.has(configuredTab)) {
          this.exportSuccessTab = configuredTab;
        }
      }
      if (this.data.topFilters && Array.isArray(this.data.topFilters)) {
        this.topFilters = this.data.topFilters.map((f: any) => ({
          label: f.label,
          key: f.key,
          options: [] as any[],
          optionsMap: {}
        }));
      }
    }

    const apiUrl = this.configService.getPlatformApiUrl();
    console.log('[DeepDive] ngOnInit - platformApiUrl:', apiUrl, 'entityName:', this.entityName, 'filterEntity:', this.filterEntity);
    const queryParamLoadAll = this.parseBooleanQueryParam(this.route.snapshot.queryParams?.['loadAllTopFiltersOnInit']);
    if (queryParamLoadAll !== null) {
      this.loadAllTopFiltersOnInit = queryParamLoadAll;
    }
    this.simulationName = this.getSimulationNameFromRoute();
    this.simulationUuid = this.getSimulationUuidFromRoute() || this.simulationService.getLastRunUuid() || '';
    this.subscriptions.add(
      this.route.queryParams.subscribe(() => {
        const previousSimulationUuid = this.simulationUuid;
        const latestQueryParamLoadAll = this.parseBooleanQueryParam(this.route.snapshot.queryParams?.['loadAllTopFiltersOnInit']);
        if (latestQueryParamLoadAll !== null) {
          this.loadAllTopFiltersOnInit = latestQueryParamLoadAll;
        }
        this.simulationName = this.getSimulationNameFromRoute();
        this.simulationUuid = this.getSimulationUuidFromRoute() || this.simulationService.getLastRunUuid() || '';
        if (this.simulationUuid !== previousSimulationUuid) {
          this.fetchSavedReports();
        }
      })
    );
    this.simulationFilterContext = this.normalizeSimulationFilterContext(this.simulationService.getDeepDiveFilterContext());
    this.subscriptions.add(
      this.simulationService.deepDiveFilterContext$.subscribe((ctx) => {
        const normalizedContext = this.normalizeSimulationFilterContext(ctx);
        const hasContextChanged = !this.isSameSimulationFilterContext(this.simulationFilterContext, normalizedContext);
        this.simulationFilterContext = normalizedContext;

        // Re-fetch top filter dropdowns when Run response context arrives/changes.
        if (hasContextChanged && this.hasLoadedTopFilters) {
          this.refreshTopFilterOptions();
        }
      })
    );
    const initialDeepDiveDetailsResponse = this.simulationService.getDeepDiveDetailsResponse();
    this.updateDeepDiveEntityContext(initialDeepDiveDetailsResponse);
    this.applyPrefilledTopFiltersFromDetails(initialDeepDiveDetailsResponse);
    this.subscriptions.add(
      this.simulationService.deepDiveDetailsResponse$.subscribe((response) => {
        this.updateDeepDiveEntityContext(response);
        this.applyPrefilledTopFiltersFromDetails(response);
      })
    );
    this.subscriptions.add(
      this.simulationService.simulationResults$.subscribe((results) => {
        this.simulationResults = results;
      })
    );

    this.fetchAvailableColumns();
    if (this.loadAllTopFiltersOnInit) {
      this.refreshTopFilterOptions();
    }
    // Keep top filters prefilled initially; if disabled, fetch full options lazily on dropdown open.
    // Fetch any pre-defined saved reports
    this.fetchSavedReports();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.pendingTopFilterReload) {
      clearTimeout(this.pendingTopFilterReload);
      this.pendingTopFilterReload = null;
    }
    if (this.pendingTopFilterSelectionRefresh) {
      clearTimeout(this.pendingTopFilterSelectionRefresh);
      this.pendingTopFilterSelectionRefresh = null;
    }
    if (this.pendingGrandTotalLayoutSync) {
      clearTimeout(this.pendingGrandTotalLayoutSync);
      this.pendingGrandTotalLayoutSync = null;
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.queueGrandTotalLayoutSync();
  }

  onReportTableScroll() {
    const scroller = this.reportTableScroller?.nativeElement;
    if (!scroller) {
      return;
    }
    this.reportTableScrollLeft = scroller.scrollLeft;
  }

  private queueGrandTotalLayoutSync() {
    if (this.pendingGrandTotalLayoutSync) {
      clearTimeout(this.pendingGrandTotalLayoutSync);
    }

    this.pendingGrandTotalLayoutSync = setTimeout(() => {
      this.pendingGrandTotalLayoutSync = null;
      this.syncGrandTotalLayout();
    }, 0);
  }

  private syncGrandTotalLayout() {
    const scroller = this.reportTableScroller?.nativeElement;
    if (!scroller || this.activeTab !== 'report-view' || this.activeReportColumns.length === 0) {
      this.headerColumnWidths = [];
      this.reportTableContentWidth = 0;
      this.reportTableScrollLeft = 0;
      return;
    }

    const headerCells = this.reportHeaderCells?.toArray() || [];
    this.headerColumnWidths = headerCells.map((cellRef) =>
      Math.ceil(cellRef.nativeElement.getBoundingClientRect().width)
    );
    this.reportTableContentWidth = Math.max(scroller.scrollWidth, scroller.clientWidth);
    this.reportTableScrollLeft = scroller.scrollLeft;
    this.cdr.detectChanges();
  }

  // --- API Integrations ---
  private normalizeTopFilterName(filterName: string): string {
    const normalized = String(filterName || '').trim();
    return normalized === 'dn_pricing_values' ? 'dn_values' : (normalized || 'dn_values');
  }

  private normalizeSimulationEntity(entity: any): string {
    const rawValue = String(entity || '').trim().toLowerCase();
    if (!rawValue) {
      return '';
    }

    const withoutCodePrefix = rawValue.replace(/^\d+\s*[-:]\s*/, '').trim();
    const normalized = withoutCodePrefix
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    if (!normalized) {
      return '';
    }
    if (normalized === 'base_price') {
      return 'base_price';
    }
    if (normalized === 'dn_pricing') {
      return 'dn_pricing';
    }

    return normalized;
  }

  private extractSimulationEntityFromDetailsResponse(response: any): string {
    const candidates = [
      response?.requestData?.source_entity,
      response?.requestData?.entity,
      response?.responseList?.[0]?.requestData?.source_entity,
      response?.responseList?.[0]?.requestData?.entity,
      response?.responseList?.[0]?.simulation?.[0]?.data?.source_entity,
      response?.responseList?.[0]?.simulation?.[0]?.data?.entity,
      response?.responseList?.[0]?.summary?.[0]?.data?.source_entity,
      this.simulationService.getEntity(),
      this.loadRequestEntity,
      this.filterEntity
    ];

    for (const candidate of candidates) {
      const normalized = this.normalizeSimulationEntity(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return '';
  }

  private updateDeepDiveEntityContext(response: any) {
    const resolvedEntity = this.extractSimulationEntityFromDetailsResponse(response);
    if (resolvedEntity) {
      this.selectedSimulationEntity = resolvedEntity;
    }
  }

  private resolveTopFilterRequestName(): string {
    // If a filterName was explicitly provided in the dashboard JSON, use it.
    const configuredName = String(this.filterNameValue || '').trim();
    if (configuredName && configuredName !== 'dn_values') {
      return configuredName;
    }

    const selectedEntity = String(this.selectedSimulationEntity || '').trim().toLowerCase();
    const dropdownEntity = String(this.filterEntity || '').trim().toLowerCase();

    if (selectedEntity === 'base_price' || dropdownEntity === 'base_price') {
      return 'distinct_dn_base_values';
    }
    if (selectedEntity === 'dn_pricing' || dropdownEntity === 'dn_pricing') {
      return 'distinct_dn_pricing';
    }
    return this.filterNameValue || 'dn_values';
  }

  private buildLoadContextPayload(entityType: string): { contextType: string; } | null {
    const normalizedEntity = String(entityType || '').trim().toLowerCase();
    if (normalizedEntity === 'base_price') {
      return { contextType: 'oem' };
    }
    if (normalizedEntity === 'dn_pricing') {
      return { contextType: 'region' };
    }
    return null;
  }

  private resolveSimulationRequestEntity(fallbackEntity?: string): string {
    const selectedEntity = String(this.selectedSimulationEntity || '').trim().toLowerCase();
    if (selectedEntity === 'base_price' || selectedEntity === 'dn_pricing') {
      return selectedEntity;
    }

    const configuredFallback = String(fallbackEntity || '').trim().toLowerCase();
    if (configuredFallback) {
      return configuredFallback;
    }

    return this.columnEntity.includes('oem') ? 'base_price' : 'dn_pricing';
  }

  private resolveLoadRequestEntity(): string {
    return this.resolveSimulationRequestEntity(this.loadRequestEntity);
  }

  private resolveExportRequestEntity(): string {
    return this.resolveSimulationRequestEntity(this.exportRequestEntity || this.loadRequestEntity);
  }

  private resolveExportPayloadEntity(): string {
    return String(this.exportPayloadEntity || '').trim() || this.resolveExportRequestEntity();
  }

  private resolveExportColumnsKey(): string {
    const configuredColumnsKey = String(this.exportColumnsKey || '').trim();
    if (configuredColumnsKey) {
      return configuredColumnsKey;
    }

    return this.resolveExportRequestEntity() === 'base_price' ? 'pasOEMSelectedColumns' : 'selected_columns';
  }

  private parseBooleanQueryParam(value: any): boolean | null {
    const normalizedValue = String(value ?? '').trim().toLowerCase();
    if (normalizedValue === 'true' || normalizedValue === '1') {
      return true;
    }
    if (normalizedValue === 'false' || normalizedValue === '0') {
      return false;
    }
    return null;
  }

  private normalizeManualFilterValues(rawValue: any, allowWhitespaceSplit = false): string[] {
    const normalizeList = (values: any[]): string[] => values
      .map((value: any) => String(value ?? '').trim().replace(/^['"]|['"]$/g, ''))
      .filter((value: string) => value !== '');

    if (Array.isArray(rawValue)) {
      return normalizeList(rawValue);
    }

    const raw = String(rawValue ?? '').trim();
    if (!raw) {
      return [];
    }

    if (raw.startsWith('[') && raw.endsWith(']')) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return normalizeList(parsed);
        }
      } catch {
        // Fall through to non-JSON list parsing (e.g. [a, b]).
      }

      const inner = raw.slice(1, -1).trim();
      if (!inner) {
        return [];
      }
      return inner
        .split(',')
        .map((value: string) => value.trim().replace(/^['"]|['"]$/g, ''))
        .filter((value: string) => value !== '');
    }

    if (allowWhitespaceSplit && raw.includes(',')) {
      return raw
        .split(',')
        .map((value: string) => value.trim())
        .filter((value: string) => value !== '');
    }

    if (allowWhitespaceSplit) {
      const parts = raw
        .split(/\s+/)
        .map((value: string) => value.trim())
        .filter((value: string) => value !== '');
      return parts.length > 1 ? parts : [raw];
    }

    return [raw];
  }

  private normalizeFilterPayloadValue(rawValue: any, forceArray = false, allowWhitespaceSplit = false): any {
    const normalizedValues = this.normalizeManualFilterValues(rawValue, allowWhitespaceSplit);
    if (normalizedValues.length === 0) {
      return forceArray ? [] : '';
    }
    if (forceArray) {
      return normalizedValues;
    }
    if (normalizedValues.length === 1) {
      return normalizedValues[0];
    }
    return normalizedValues;
  }

  private isListOperator(operator: any): boolean {
    const op = String(operator || '').trim().toLowerCase();
    return op === 'in' || op === 'not_in' || op === 'not in';
  }

  private normalizeTopFilterPayloadValue(filterKey: string, rawValue: any, forceArray = false): any {
    const filterDef = { key: filterKey };
    if (this.isPartNumberFilter(filterDef)) {
      return this.normalizeManualFilterValues(rawValue, true);
    }
    if (forceArray) {
      return this.normalizeManualFilterValues(rawValue, false);
    }
    return rawValue;
  }

  private getTopFilterRequestContext() {
    const requestContext: any = {};
    const uuid = String(this.simulationUuid || '').trim();
    if (uuid) {
      requestContext.uuid = uuid;
      requestContext.simulation_uuid = uuid;
    }
    if (this.simulationFilterContext.part_number_list.length > 0) {
      requestContext.part_number_list = this.simulationFilterContext.part_number_list;
    }
    if (this.simulationFilterContext.region_list.length > 0) {
      requestContext.region_list = this.simulationFilterContext.region_list;
    }
    return requestContext;
  }

  private normalizeSimulationFilterContext(ctx: any): { part_number_list: string[]; region_list: string[] } {
    const normalizeList = (list: any): string[] => {
      if (!Array.isArray(list)) {
        return [];
      }
      return list
        .map((value: any) => String(value ?? '').trim())
        .filter((value: string) => value !== '');
    };

    return {
      part_number_list: normalizeList(ctx?.part_number_list),
      region_list: normalizeList(ctx?.region_list)
    };
  }

  private isSameSimulationFilterContext(
    a: { part_number_list: string[]; region_list: string[] },
    b: { part_number_list: string[]; region_list: string[] }
  ): boolean {
    return JSON.stringify(a?.part_number_list || []) === JSON.stringify(b?.part_number_list || [])
      && JSON.stringify(a?.region_list || []) === JSON.stringify(b?.region_list || []);
  }

  private refreshTopFilterOptions() {
    this.hasLoadedTopFilters = true;
    this.fetchTopFilterOptions();
  }

  isPartNumberFilter(filterDef: any): boolean {
    const normalizedKey = String(filterDef?.key || '').trim().toLowerCase();
    return normalizedKey === 'part_number' || normalizedKey === 'partnumber' || normalizedKey === 'part number';
  }

  onTopFilterDropdownToggle(filterDef: any, isOpened: boolean) {
    if (!isOpened || !filterDef || this.isPartNumberFilter(filterDef)) {
      return;
    }

    // this.fetchTopFilterOptions(filterDef);
  }

  private getTopFilterComparisonTokens(value: any): string[] {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return [];
    }

    const tokens = new Set<string>();
    const addToken = (token: any) => {
      const normalized = String(token ?? '').trim().toLowerCase();
      if (!normalized) {
        return;
      }
      tokens.add(normalized);
      const numericValue = Number(normalized);
      if (Number.isFinite(numericValue)) {
        tokens.add(String(numericValue));
      }
    };

    addToken(raw);
    return Array.from(tokens);
  }

  private parseBracedTopFilterValue(value: any): { code: string; label: string } | null {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return null;
    }
    const braceMatch = raw.match(/^\s*([^{}]+)\{([^}]+)\}\s*$/);
    if (!braceMatch) {
      return null;
    }
    return {
      code: String(braceMatch[1] || '').trim().toLowerCase(),
      label: String(braceMatch[2] || '').trim().toLowerCase()
    };
  }

  private areTopFilterValuesEquivalent(left: any, right: any): boolean {
    const leftRaw = String(left ?? '').trim().toLowerCase();
    const rightRaw = String(right ?? '').trim().toLowerCase();
    if (leftRaw && rightRaw && leftRaw === rightRaw) {
      return true;
    }

    const leftBrace = this.parseBracedTopFilterValue(left);
    const rightBrace = this.parseBracedTopFilterValue(right);
    if (leftBrace && rightBrace) {
      return leftBrace.code === rightBrace.code && leftBrace.label === rightBrace.label;
    }
    if (leftBrace && !rightBrace) {
      return leftBrace.code === rightRaw || leftBrace.label === rightRaw;
    }
    if (!leftBrace && rightBrace) {
      return rightBrace.code === leftRaw || rightBrace.label === leftRaw;
    }

    const leftTokens = this.getTopFilterComparisonTokens(left);
    const rightTokens = new Set(this.getTopFilterComparisonTokens(right));
    return leftTokens.some((token) => rightTokens.has(token));
  }

  private areTopFilterSelectionsEquivalent(left: any[], right: any[]): boolean {
    if (left.length !== right.length) {
      return false;
    }
    return left.every((leftValue: any) =>
      right.some((rightValue: any) => this.areTopFilterValuesEquivalent(leftValue, rightValue))
    );
  }

  private mergeTopFilterValues(selectedValues: any[], requiredValues: any[]): any[] {
    const merged = Array.isArray(selectedValues) ? [...selectedValues] : [];

    requiredValues.forEach((requiredValue: any) => {
      const exists = merged.some((value: any) => this.areTopFilterValuesEquivalent(value, requiredValue));
      if (!exists) {
        merged.push(requiredValue);
      }
    });

    return merged.filter((value: any, index: number, all: any[]) =>
      String(value ?? '').trim() !== ''
      && all.findIndex((candidate: any) => this.areTopFilterValuesEquivalent(candidate, value)) === index
    );
  }

  private getResolvedLockedTopFilterValues(filterKey: string): any[] {
    const lockedValues = this.lockedTopFilterValues[filterKey] || [];
    if (lockedValues.length === 0) {
      return [];
    }

    return lockedValues
      .map((value: any) => this.resolvePrefillOptionValue(filterKey, value))
      .reduce((acc: any[], value: any) => acc.concat(Array.isArray(value) ? value : [value]), [])
      .map((value: any) => String(value ?? '').trim())
      .filter((value: string) => value !== '')
      .filter((value: string, index: number, all: string[]) =>
        all.findIndex((candidate: string) => this.areTopFilterValuesEquivalent(candidate, value)) === index
      );
  }

  isLockedTopFilterOption(filterKey: string, option: any): boolean {
    return this.getResolvedLockedTopFilterValues(filterKey)
      .some((lockedValue: any) => this.areTopFilterValuesEquivalent(lockedValue, option));
  }

  onTopFilterOptionMouseDown(filterKey: string, option: any, event: MouseEvent, select: MatSelect) {
    if (this.isLockedTopFilterOption(filterKey, option)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const currentValue = this.selectedTopFilters[filterKey];
    if (currentValue !== option) {
      return;
    }

    // Intercept before mat-select applies selection so same-option click reliably deselects.
    event.preventDefault();
    event.stopPropagation();
    this.onTopFilterValueChange(filterKey, null);
    this.cdr.detectChanges();
    setTimeout(() => select.close());
  }

  onTopFilterValueChange(filterKey: string, value: any) {
    console.log(this.TOP_FILTER_DEBUG_KEY, 'onTopFilterValueChange.input', {
      filterKey,
      rawValue: value,
      selectedTopFiltersBefore: { ...this.selectedTopFilters }
    });
    const filterDef = this.topFilters.find((filter: any) => String(filter?.key || '').trim() === String(filterKey || '').trim());
    const previousValue = this.selectedTopFilters[filterKey];
    let hasChanged = false;
    if (filterDef && !this.isPartNumberFilter(filterDef)) {
      const normalizedValues = this.normalizeManualFilterValues(value, false);
      const lockedValues = this.getResolvedLockedTopFilterValues(filterKey);
      const mergedValues = this.mergeTopFilterValues(normalizedValues, lockedValues);
      const previousValues = this.normalizeManualFilterValues(previousValue, false);
      hasChanged = !this.areTopFilterSelectionsEquivalent(previousValues, mergedValues);
      this.selectedTopFilters[filterKey] = mergedValues;
      this.appliedTopFilterOperators[filterKey] = mergedValues.length > 0 ? 'in' : '';
    } else {
      hasChanged = String(previousValue ?? '') !== String(value ?? '');
      this.selectedTopFilters[filterKey] = value;
    }
    if (!hasChanged) {
      return;
    }
    this.userModifiedTopFilters[filterKey] = true;
    if (Object.prototype.hasOwnProperty.call(this.pendingTopFilterPrefill, filterKey)) {
      delete this.pendingTopFilterPrefill[filterKey];
    }
    console.log(this.TOP_FILTER_DEBUG_KEY, 'onTopFilterValueChange.output', {
      filterKey,
      selectedTopFiltersAfter: { ...this.selectedTopFilters },
      appliedTopFilterOperators: { ...this.appliedTopFilterOperators }
    });

    this.queueTopFilterSelectionRefresh(filterKey);
    this.queueReportReloadOnTopFilterChange();
  }

  private queueTopFilterSelectionRefresh(filterKey: string): void {
    const filterDef = this.topFilters.find((filter: any) => String(filter?.key || '').trim() === String(filterKey || '').trim());
    if (!filterDef || this.isPartNumberFilter(filterDef)) {
      return;
    }

    // Fire an additional dropdown API after selection so dependent options refresh immediately.
    if (this.pendingTopFilterSelectionRefresh) {
      clearTimeout(this.pendingTopFilterSelectionRefresh);
    }

    this.pendingTopFilterSelectionRefresh = setTimeout(() => {
      this.pendingTopFilterSelectionRefresh = null;
      const shouldPreserveTriggerFilterOptions = this.hasMeaningfulFilterValue(this.selectedTopFilters[filterKey]);
      this.fetchTopFilterOptions(filterDef, { preserveTriggerFilterOptions: shouldPreserveTriggerFilterOptions });
    }, 0);
  }

  private queueReportReloadOnTopFilterChange(): void {
    // Reuse the same API/payload path as the Load button when top filters change.
    // Only auto-refresh report data while the loaded report is visible.
    if (this.activeTab !== 'report-view') {
      return;
    }

    if (this.isReportLoadInProgress) {
      return;
    }

    const selectedColumns = this.getSelectedColumnsForFilterTriggeredLoad();
    if (selectedColumns.length === 0) {
      return;
    }

    if (this.pendingTopFilterReload) {
      clearTimeout(this.pendingTopFilterReload);
    }

    this.pendingTopFilterReload = setTimeout(() => {
      this.pendingTopFilterReload = null;
      this.loadReport(selectedColumns);
    }, 150);
  }

  private getSelectedColumnsForFilterTriggeredLoad(): string[] {
    if (this.selectedReport) {
      return this.getSavedColumnsFromRowData(this.selectedReport);
    }

    const fromColumnSelection = (this.selectedColumns || [])
      .map((column: any) => this.getSelectedColumnLabel(column))
      .map((label: string) => String(label || '').trim())
      .filter((label: string) => label !== '');

    if (fromColumnSelection.length > 0) {
      return Array.from(new Set(fromColumnSelection));
    }

    const defaultKeys = new Set(this.defaultReportColumns.map((c: any) => this.getCanonicalReportKey(c.key)));
    const fromLoadedReport = (this.activeReportColumns || [])
      .map((column: any) => String(column?.originalKey || column?.label || column?.key || '').trim())
      .filter((label: string) => label !== '')
      .filter((label: string) => !defaultKeys.has(this.getCanonicalReportKey(label)));

    return Array.from(new Set(fromLoadedReport));
  }

  private resolveSavedFilterCreatedBy(): string {
    const createdByFromSimulation = String(this.simulationName || '').trim();
    if (createdByFromSimulation) {
      return createdByFromSimulation;
    }

    const createdByFromConfig = String(this.savedFilterCreatedBy || '').trim();
    if (createdByFromConfig) {
      return createdByFromConfig;
    }

    return '';
  }

  private parseAppliedFilters(rawFilters: any): Array<{
    field: string;
    operator: string;
    value: string | string[];
    logicalOperator?: '' | 'AND' | 'OR';
    beginScope?: string;
    endScope?: string;
  }> {
    const isListOperator = (operator: any): boolean => {
      const op = String(operator || '').trim().toLowerCase();
      return op === 'in' || op === 'not_in' || op === 'not in';
    };

    if (Array.isArray(rawFilters)) {
      type ParsedAppliedFilter = {
        field: string;
        operator: string;
        value: string | string[];
        logicalOperator: '' | 'AND' | 'OR';
        beginScope: string;
        endScope: string;
      };
      return rawFilters
        .map((item: any): ParsedAppliedFilter => {
          const operator = String(item?.operator || '=').trim() || '=';
          const forceArray = isListOperator(operator);
          return {
            field: String(item?.field || item?.attributeName || '').trim(),
            operator,
            value: this.normalizeFilterPayloadValue(
              Array.isArray(item?.attributeValues) ? item?.attributeValues : item?.value,
              forceArray
            ),
            logicalOperator: (() : '' | 'AND' | 'OR' => {
            const op = String(item?.logicalOperator || '').trim().toUpperCase();
            return op === 'AND' || op === 'OR' ? op : '';
          })(),
            beginScope: String(item?.beginScope || ''),
            endScope: String(item?.endScope || '')
          };
        })
        .filter((item: any) => item.field !== '' && (Array.isArray(item.value) ? item.value.length > 0 : item.value !== ''));
    }

    const serializedFilters = String(rawFilters ?? '').trim();
    if (!serializedFilters) {
      return [];
    }

    try {
      const parsed = JSON.parse(serializedFilters);
      if (Array.isArray(parsed)) {
        return this.parseAppliedFilters(parsed);
      }
    } catch {
      // Handle backend list syntax like:
      // [{field=region, value=Brazil}, {field=product_bu, value=CES}]
    }

    const blocks = serializedFilters.match(/\{[^{}]+\}/g) || [];
    return blocks
      .map((block: string): {
        field: string;
        operator: string;
        value: string | string[];
        logicalOperator: '' | 'AND' | 'OR';
        beginScope: string;
        endScope: string;
      } => {
        const fieldMatch = block.match(/(?:field|attributeName)\s*=\s*([^,}]+)/i);
        const operatorMatch = block.match(/operator\s*=\s*([^,}]+)/i);
        const valueMatch = block.match(/(?:value|attributeValues)\s*=\s*([^,}]+)/i);
        const operator = String(operatorMatch?.[1] || '=').trim() || '=';
        const forceArray = isListOperator(operator);
        return {
          field: String(fieldMatch?.[1] || '').trim(),
          operator,
          value: this.normalizeFilterPayloadValue(String(valueMatch?.[1] || '').trim(), forceArray),
          logicalOperator: '',
          beginScope: '',
          endScope: ''
        };
      })
      .filter((item: any) => item.field !== '' && (Array.isArray(item.value) ? item.value.length > 0 : item.value !== ''));
  }

  private normalizeFilterKeyToken(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private resolveTopFilterKey(rawFieldKey: any): string {
    const rawValue = String(rawFieldKey ?? '').trim();
    if (!rawValue) {
      return '';
    }

    const exactMatch = this.topFilters.find((filter: any) => String(filter?.key || '').trim() === rawValue);
    if (exactMatch?.key) {
      return String(exactMatch.key).trim();
    }

    const caseInsensitiveMatch = this.topFilters.find((filter: any) =>
      String(filter?.key || '').trim().toLowerCase() === rawValue.toLowerCase()
    );
    if (caseInsensitiveMatch?.key) {
      return String(caseInsensitiveMatch.key).trim();
    }

    const normalizedRaw = this.normalizeFilterKeyToken(rawValue);
    if (!normalizedRaw) {
      return '';
    }

    const normalizedMatch = this.topFilters.find((filter: any) =>
      this.normalizeFilterKeyToken(filter?.key) === normalizedRaw
    );
    if (normalizedMatch?.key) {
      return String(normalizedMatch.key).trim();
    }

    return '';
  }

  private clearTopFilterSelections() {
    this.topFilters.forEach((filter: any) => {
      const key = String(filter?.key || '').trim();
      if (key && Object.prototype.hasOwnProperty.call(this.selectedTopFilters, key)) {
        delete this.selectedTopFilters[key];
      }
    });
  }

  private extractAppliedFiltersFromDetailsResponse(response: any): Array<{ field: string; operator: string; value: string | string[] }> {
    const candidates = [
      response?.requestData?.appliedFilters,
      response?.requestData?.deep_dive_applied_filters,
      response?.requestData?.filter?.where,
      response?.responseList?.[0]?.requestData?.appliedFilters,
      response?.responseList?.[0]?.requestData?.deep_dive_applied_filters,
      response?.responseList?.[0]?.requestData?.filter?.where,
      response?.responseList?.[0]?.simulation?.[0]?.data?.appliedFilters,
      response?.responseList?.[0]?.simulation?.[0]?.data?.deep_dive_applied_filters
    ];

    for (const candidate of candidates) {
      const parsed = this.parseAppliedFilters(candidate);
      if (parsed.length > 0) {
        return parsed;
      }
    }

    return [];
  }

  private resolvePrefillOptionValue(filterKey: string, rawValue: any): any {
    if (Array.isArray(rawValue)) {
      return rawValue.map((value: any) => this.resolvePrefillOptionValue(filterKey, value));
    }

    const normalizedRawValue = String(rawValue ?? '').trim();
    if (!normalizedRawValue) {
      return rawValue;
    }

    const filterDef = this.topFilters.find((filter: any) => filter.key === filterKey);
    if (!filterDef || !Array.isArray(filterDef.options) || filterDef.options.length === 0) {
      return normalizedRawValue;
    }

    const exactMatch = filterDef.options.find((option: any) => String(option).trim() === normalizedRawValue);
    if (exactMatch !== undefined) {
      return exactMatch;
    }

    const lowerRawValue = normalizedRawValue.toLowerCase();
    const fuzzyMatch = filterDef.options.find((option: any) => {
      const optionText = String(option).trim();
      if (optionText.toLowerCase() === lowerRawValue) {
        return true;
      }

      const braceMatch = optionText.match(/^\s*([^{}]+)\{([^}]+)\}\s*$/);
      if (!braceMatch) {
        return false;
      }

      const optionCode = braceMatch[1].trim().toLowerCase();
      const optionLabel = braceMatch[2].trim().toLowerCase();
      return optionCode === lowerRawValue || optionLabel === lowerRawValue;
    });

    return fuzzyMatch !== undefined ? fuzzyMatch : normalizedRawValue;
  }

  private applyPendingTopFilterPrefill() {
    const pendingKeys = Object.keys(this.pendingTopFilterPrefill || {});
    if (pendingKeys.length === 0) {
      return;
    }
    console.log(this.TOP_FILTER_DEBUG_KEY, 'applyPendingTopFilterPrefill.start', {
      pendingTopFilterPrefill: { ...this.pendingTopFilterPrefill },
      userModifiedTopFilters: { ...this.userModifiedTopFilters },
      selectedTopFiltersBefore: { ...this.selectedTopFilters }
    });

    pendingKeys.forEach((filterKey: string) => {
      if (this.userModifiedTopFilters[filterKey]) {
        delete this.pendingTopFilterPrefill[filterKey];
        return;
      }
      const rawValue = this.pendingTopFilterPrefill[filterKey];
      const resolvedValue = this.resolvePrefillOptionValue(filterKey, rawValue);
      const filterDef = this.topFilters.find((filter: any) => filter.key === filterKey);
      if (filterDef && !this.isPartNumberFilter(filterDef)) {
        const normalizedValues = this.normalizeManualFilterValues(resolvedValue, false);
        const lockedValues = this.getResolvedLockedTopFilterValues(filterKey);
        const mergedValues = this.mergeTopFilterValues(normalizedValues, lockedValues);
        this.selectedTopFilters[filterKey] = mergedValues;
        this.appliedTopFilterOperators[filterKey] = mergedValues.length > 0 ? 'in' : '';
      } else {
        this.selectedTopFilters[filterKey] = resolvedValue;
      }
    });
    this.ensurePrefillOptionsForSelectedFilters();
    console.log(this.TOP_FILTER_DEBUG_KEY, 'applyPendingTopFilterPrefill.end', {
      selectedTopFiltersAfter: { ...this.selectedTopFilters },
      appliedTopFilterOperators: { ...this.appliedTopFilterOperators }
    });
    this.cdr.detectChanges();
  }

  private ensurePrefillOptionsForSelectedFilters() {
    Object.keys(this.selectedTopFilters || {}).forEach((filterKey: string) => {
      const selectedValue = this.selectedTopFilters[filterKey];
      if (selectedValue === undefined || selectedValue === null) {
        return;
      }

      const filterDef = this.topFilters.find((filter: any) => filter.key === filterKey);
      if (!filterDef) {
        return;
      }

      const existingOptions = Array.isArray(filterDef.options) ? filterDef.options : [];
      const valuesToEnsure = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
      valuesToEnsure
        .map((value: any) => String(value ?? '').trim())
        .filter((value: string) => value !== '')
        .forEach((value: string) => {
          if (!existingOptions.includes(value)) {
            existingOptions.unshift(value);
          }
        });
      filterDef.options = existingOptions;
    });
  }

  private reapplyLockedTopFilterSelections() {
    Object.keys(this.lockedTopFilterValues || {}).forEach((filterKey: string) => {
      const filterDef = this.topFilters.find((filter: any) => filter.key === filterKey);
      if (!filterDef || this.isPartNumberFilter(filterDef)) {
        return;
      }

      const lockedValues = this.getResolvedLockedTopFilterValues(filterKey);
      if (lockedValues.length === 0) {
        return;
      }

      this.selectedTopFilters[filterKey] = lockedValues;
      this.appliedTopFilterOperators[filterKey] = 'in';
    });
    this.ensurePrefillOptionsForSelectedFilters();
  }

  private applyPrefilledTopFiltersFromDetails(response: any) {
    if (!response) {
      return;
    }

    const parsedAppliedFilters = this.extractAppliedFiltersFromDetailsResponse(response);
    console.log(this.TOP_FILTER_DEBUG_KEY, 'applyPrefilledTopFiltersFromDetails.parsedAppliedFilters', parsedAppliedFilters);
    if (parsedAppliedFilters.length === 0) {
      this.deepDiveAppliedFilters = [];
      this.appliedTopFilterOperators = {};
      this.lockedTopFilterValues = {};
      if (this.prefillAppliedFiltersFromDetails) {
        this.pendingTopFilterPrefill = {};
        this.userModifiedTopFilters = {};
        this.clearTopFilterSelections();
        this.cdr.detectChanges();
      }
      return;
    }

    this.deepDiveAppliedFilters = parsedAppliedFilters.map((filter: {
      field: string;
      operator: string;
      value: string | string[];
      logicalOperator?: '' | 'AND' | 'OR';
      beginScope?: string;
      endScope?: string;
    }) => ({
      field: String(filter?.field || '').trim(),
      operator: String(filter?.operator || '=').trim() || '=',
      value: this.normalizeFilterPayloadValue(
        filter?.value,
        this.isListOperator(filter?.operator || '=')
      ),
      logicalOperator: (() => {
        const op = String(filter?.logicalOperator || '').trim().toUpperCase();
        return op === 'AND' || op === 'OR' ? op : '';
      })(),
      beginScope: String(filter?.beginScope || ''),
      endScope: String(filter?.endScope || '')
    }));
    console.log(this.TOP_FILTER_DEBUG_KEY, 'applyPrefilledTopFiltersFromDetails.deepDiveAppliedFilters', this.deepDiveAppliedFilters);
    this.appliedTopFilterOperators = {};
    this.deepDiveAppliedFilters.forEach((filter: { field: string; operator: string }) => {
      const resolvedTopFilterKey = this.resolveTopFilterKey(filter.field);
      const operatorKey = resolvedTopFilterKey || filter.field;
      if (!operatorKey) {
        return;
      }
      this.appliedTopFilterOperators[operatorKey] = filter.operator || '=';
    });

    if (!this.prefillAppliedFiltersFromDetails) {
      this.userModifiedTopFilters = {};
      this.lockedTopFilterValues = {};
      this.clearTopFilterSelections();
      this.pendingTopFilterPrefill = {};
      console.log(this.TOP_FILTER_DEBUG_KEY, 'applyPrefilledTopFiltersFromDetails.prefillDisabled.clearSelection', {
        selectedTopFiltersAfterClear: { ...this.selectedTopFilters },
        pendingTopFilterPrefillAfterClear: { ...this.pendingTopFilterPrefill }
      });
      this.cdr.detectChanges();
      return;
    }

    this.userModifiedTopFilters = {};
    this.lockedTopFilterValues = {};
    this.clearTopFilterSelections();
    this.pendingTopFilterPrefill = {};
    const prefillByFilterKey: { [key: string]: string[] } = {};
    parsedAppliedFilters.forEach((filter: { field: string; operator: string; value: string | string[] }) => {
      if (this.shouldSkipTopFilterPrefillForOperator(filter?.operator)) {
        return;
      }
      const filterKey = this.resolveTopFilterKey(filter?.field);
      if (!filterKey) {
        return;
      }
      // Do not split by whitespace here; values like "25 {Rest Of World}"
      // must remain a single dropdown value.
      const normalizedFilterValues = this.normalizeManualFilterValues(filter?.value, false);
      if (normalizedFilterValues.length === 0) {
        return;
      }
      if (!prefillByFilterKey[filterKey]) {
        prefillByFilterKey[filterKey] = [];
      }
      normalizedFilterValues.forEach((value: string) => {
        if (!prefillByFilterKey[filterKey].includes(value)) {
          prefillByFilterKey[filterKey].push(value);
        }
      });
    });

    Object.keys(prefillByFilterKey).forEach((filterKey: string) => {
      const values = prefillByFilterKey[filterKey] || [];
      const filterDef = this.topFilters.find((filter: any) => filter.key === filterKey);
      if (filterDef && !this.isPartNumberFilter(filterDef)) {
        this.pendingTopFilterPrefill[filterKey] = values;
        this.lockedTopFilterValues[filterKey] = [...values];
      } else {
        this.pendingTopFilterPrefill[filterKey] = values.join(', ');
      }
    });
    console.log(this.TOP_FILTER_DEBUG_KEY, 'applyPrefilledTopFiltersFromDetails.pendingTopFilterPrefillPrepared', {
      pendingTopFilterPrefill: { ...this.pendingTopFilterPrefill }
    });

    this.applyPendingTopFilterPrefill();
  }

  private parseSavedColumns(columnsValue: any): string[] {
    const normalizeColumn = (value: any): string => String(value ?? '').trim().replace(/^['"]|['"]$/g, '');

    if (Array.isArray(columnsValue)) {
      return columnsValue.map(normalizeColumn).filter((value: string) => value !== '');
    }

    const rawColumns = String(columnsValue ?? '').trim();
    if (!rawColumns) {
      return [];
    }

    // Handle true JSON arrays first (e.g. ["current_sales","current_part_number"]).
    try {
      const parsed = JSON.parse(rawColumns);
      if (Array.isArray(parsed)) {
        return parsed.map(normalizeColumn).filter((value: string) => value !== '');
      }
      if (typeof parsed === 'string') {
        return this.parseSavedColumns(parsed);
      }
    } catch {
      // Fall through to bracket/comma parser.
    }

    // Handle legacy format like "[current_sales, current_part_number]".
    const withoutBrackets = rawColumns.replace(/^\[/, '').replace(/\]$/, '');
    return withoutBrackets
      .split(',')
      .map(part => normalizeColumn(part))
      .filter((value: string) => value !== '');
  }

  private getSavedColumnsFromRowData(rowData: any): string[] {
    const candidates = [
      rowData?.selected_columns,
      rowData?.selectedColumns,
      rowData?.selected_column,
      rowData?.selectedColumn,
      rowData?.columns,
      rowData?.payload
    ];

    for (const candidate of candidates) {
      const parsed = this.parseSavedColumns(candidate);
      if (parsed.length > 0) {
        return parsed;
      }
    }

    return [];
  }

  private buildSavedReportsFromRows(rows: any[]): any[] {
    return rows.map((r: any, index: number) => {
      const rData = r?.data || r;
      const columns = this.getSavedColumnsFromRowData(rData);
      const reportName = String(rData?.filter_name ?? rData?.name ?? '').trim();
      const reportUuid = String(rData?.uuid || '').trim();

      return {
        id: reportUuid || reportName || `saved-report-${index}`,
        uuid: reportUuid,
        simulation_uuid: rData?.simulation_uuid || '',
        name: reportName || `Saved Report ${index + 1}`,
        columns
      };
    });
  }

  private toApiColumnKey(columnName: string): string {
    return String(columnName || '')
      .trim()
      .replace(/\s+/g, '_')
      .toLowerCase();
  }

  private getCanonicalReportKey(columnKey: string): string {
    const normalizedKey = this.toApiColumnKey(columnKey);
    if (normalizedKey === 'region') {
      return 'region_name';
    }
    if (normalizedKey === 'dir_business_owner') {
      return 'business_owner';
    }
    if (normalizedKey === 'rpf_flag' || normalizedKey === 'regional_pricing_flag_code' || normalizedKey === 'regional_pricing_flag') {
      return 'rpf';
    }
    if (normalizedKey === 'impact_percent' || normalizedKey === 'impact_%') {
      return 'percentage_impact';
    }
    return normalizedKey;
  }

  private isNullLikeValue(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    const normalized = String(value).trim().toLowerCase();
    return normalized === '' || normalized === 'null' || normalized === 'undefined' || normalized === 'n/a';
  }

  private normalizeDimensionText(value: any, fallback = ''): string {
    if (this.isNullLikeValue(value)) {
      return fallback;
    }

    return String(value).trim();
  }

  private getValueByAliases(source: any, aliases: string[]): any {
    if (!source || typeof source !== 'object') {
      return undefined;
    }

    for (const alias of aliases) {
      if (source[alias] !== undefined && source[alias] !== null) {
        return source[alias];
      }
    }

    return undefined;
  }

  private normalizeFlatReportRow(row: any): any {
    if (!row || typeof row !== 'object') {
      return {};
    }

    const normalizedRow = { ...row };
    const regionValue = this.getValueByAliases(row, ['region', 'region_name']);
    if (regionValue !== undefined) {
      normalizedRow.region_name = this.normalizeDimensionText(regionValue, '');
    }

    const businessOwner = this.getValueByAliases(row, ['business_owner', 'dir_business_owner']);
    if (businessOwner !== undefined) {
      normalizedRow.business_owner = this.normalizeDimensionText(businessOwner, '');
    }

    const changeReason = this.getValueByAliases(row, ['change_reason']);
    if (changeReason !== undefined) {
      normalizedRow.change_reason = this.normalizeDimensionText(changeReason, '(No Change Reason)');
    }

    const rpfValue = this.getValueByAliases(row, ['rpf', 'rpf_flag', 'regional_pricing_flag_code', 'regional_pricing_flag']);
    if (rpfValue !== undefined) {
      normalizedRow.rpf = this.normalizeDimensionText(rpfValue, '');
    }

    const impactPercent = this.getValueByAliases(row, ['percentage_impact', 'impact_percent', 'impact_%']);
    if (impactPercent !== undefined) {
      normalizedRow.percentage_impact = impactPercent;
    }

    return normalizedRow;
  }

  private isObjectArray(value: any): boolean {
    return Array.isArray(value) && value.some((item: any) => item && typeof item === 'object');
  }

  private containsNestedObjectArrays(node: any): boolean {
    if (!node || typeof node !== 'object') {
      return false;
    }

    const values = Object.values(node);
    for (const value of values) {
      if (this.isObjectArray(value)) {
        return true;
      }
      if (value && typeof value === 'object' && this.containsNestedObjectArrays(value)) {
        return true;
      }
    }

    return false;
  }

  private flattenDynamicNode(node: any, seed: any): any[] {
    if (!node || typeof node !== 'object') {
      return [seed];
    }

    const scalarData: any = { ...seed };
    const childObjectArrays: Array<{ key: string; items: any[] }> = [];

    Object.entries(node).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (this.isObjectArray(value)) {
          childObjectArrays.push({ key, items: value as any[] });
        } else if (value.length > 0) {
          scalarData[key] = value.join(', ');
        }
        return;
      }

      if (value && typeof value === 'object') {
        const nestedRows = this.flattenDynamicNode(value, scalarData);
        nestedRows.forEach((nestedRow: any) => Object.assign(scalarData, nestedRow));
        return;
      }

      scalarData[key] = value;
    });

    if (childObjectArrays.length === 0) {
      return [scalarData];
    }

    let rows = [scalarData];
    childObjectArrays.forEach((childArray) => {
      const expandedRows: any[] = [];
      rows.forEach((rowSeed: any) => {
        if (!Array.isArray(childArray.items) || childArray.items.length === 0) {
          expandedRows.push(rowSeed);
          return;
        }

        childArray.items.forEach((childItem: any) => {
          expandedRows.push(...this.flattenDynamicNode(childItem, rowSeed));
        });
      });
      rows = expandedRows;
    });

    return rows;
  }

  private flattenDynamicReportRow(row: any): any[] {
    return this.flattenDynamicNode(row, {});
  }

  private areSameGroupValues(left: any, right: any): boolean {
    return String(left ?? '').trim() === String(right ?? '').trim();
  }

  private rowsBelongToSameGroup(currentRow: any, compareRow: any, key: string, parentKeys: string[]): boolean {
    if (!this.areSameGroupValues(currentRow?.[key], compareRow?.[key])) {
      return false;
    }

    return parentKeys.every((parentKey: string) => this.areSameGroupValues(currentRow?.[parentKey], compareRow?.[parentKey]));
  }

  private addGroupingMetadata(rows: any[], groupingHierarchy: string[]): any[] {
    const rowsWithMeta = rows.map((row: any) => ({
      ...row,
      __groupMeta: {
        showCell: {} as any,
        rowSpan: {} as any
      }
    }));

    groupingHierarchy.forEach((key: string) => {
      rowsWithMeta.forEach((row: any) => {
        row.__groupMeta.showCell[key] = true;
        row.__groupMeta.rowSpan[key] = 1;
      });
    });

    for (let groupIndex = 0; groupIndex < groupingHierarchy.length; groupIndex++) {
      const groupKey = groupingHierarchy[groupIndex];
      const parentKeys = groupingHierarchy.slice(0, groupIndex);
      let index = 0;
      while (index < rowsWithMeta.length) {
        const startRow = rowsWithMeta[index];
        let nextIndex = index + 1;
        while (
          nextIndex < rowsWithMeta.length
          && this.rowsBelongToSameGroup(startRow, rowsWithMeta[nextIndex], groupKey, parentKeys)
        ) {
          nextIndex++;
        }

        startRow.__groupMeta.rowSpan[groupKey] = nextIndex - index;
        startRow.__groupMeta.showCell[groupKey] = true;

        for (let hiddenIndex = index + 1; hiddenIndex < nextIndex; hiddenIndex++) {
          rowsWithMeta[hiddenIndex].__groupMeta.showCell[groupKey] = false;
          rowsWithMeta[hiddenIndex].__groupMeta.rowSpan[groupKey] = 0;
        }

        index = nextIndex;
      }
    }

    return rowsWithMeta;
  }

  private normalizeReportRows(apiRows: any[]): { rows: any[]; hasHierarchy: boolean } {
    const safeRows = Array.isArray(apiRows) ? apiRows : [];
    if (safeRows.length === 0) {
      return { rows: [], hasHierarchy: false };
    }

    const hasHierarchy = safeRows.some((row: any) => this.containsNestedObjectArrays(row));
    const flattenedRows = safeRows.flatMap((row: any) => this.flattenDynamicReportRow(row));
    const normalizedRows = flattenedRows.map((row: any) => this.normalizeFlatReportRow(row));
    return {
      rows: normalizedRows,
      hasHierarchy
    };
  }

  private getGroupingKeyForColumn(columnKey: string): string {
    return this.getCanonicalReportKey(columnKey);
  }

  private getReportValueByColumnKey(row: any, columnKey: string): any {
    const normalizedKey = this.toApiColumnKey(columnKey);
    const keyAliases: Record<string, string[]> = {
      region: ['region', 'region_name'],
      region_name: ['region_name', 'region'],
      business_owner: ['business_owner', 'dir_business_owner'],
      dir_business_owner: ['business_owner', 'dir_business_owner'],
      change_reason: ['change_reason'],
      rpf: ['rpf', 'rpf_flag', 'regional_pricing_flag_code', 'regional_pricing_flag'],
      rpf_flag: ['rpf', 'rpf_flag', 'regional_pricing_flag_code', 'regional_pricing_flag'],
      regional_pricing_flag_code: ['rpf', 'rpf_flag', 'regional_pricing_flag_code', 'regional_pricing_flag'],
      regional_pricing_flag: ['rpf', 'rpf_flag', 'regional_pricing_flag_code', 'regional_pricing_flag'],
      impact_percent: ['percentage_impact', 'impact_percent', 'impact_%'],
      'impact_%': ['percentage_impact', 'impact_percent', 'impact_%'],
      percentage_impact: ['percentage_impact', 'impact_percent', 'impact_%'],
      dollar_impact: ['dollar_impact', 'impact_dollar'],
      current_future_sales: ['current_future_sales', 'future_sales_current', 'total_future_sales'],
      current_impact: ['current_impact', 'impact_current', 'current_dollar_impact']
    };

    const candidates = [columnKey, normalizedKey, ...(keyAliases[normalizedKey] || [])]
      .map((key: string) => String(key || '').trim())
      .filter((key: string) => key !== '');

    for (const key of candidates) {
      if (row?.[key] !== undefined && row?.[key] !== null) {
        return row[key];
      }
    }

    return undefined;
  }

  private formatSavedColumnsForPayload(columns: string[]): string {
    const normalizedColumns = columns
      .map((col) => this.toApiColumnKey(col))
      .filter((col) => col !== '');

    return `[${normalizedColumns.join(', ')}]`;
  }

  private buildSavedFiltersForLoadPayload(): any[] {
    if (Array.isArray(this.latestSavedFilterRows) && this.latestSavedFilterRows.length > 0) {
      return this.latestSavedFilterRows;
    }

    return this.savedReports.map((report: any) => {
      const reportName = String(report?.name || '').trim();
      const reportColumns = this.getSavedColumnsFromRowData(report);
      return {
        data: {
          filter_name: reportName,
          selected_columns: this.formatSavedColumnsForPayload(reportColumns)
        },
        operation: 'NONE'
      };
    });
  }

  private extractSavedReportRows(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    const entityKeys = [this.savedFilterEntity, 'deep_dive_saved_filters'];
    const containerCandidates = [
      response,
      response?.requestData,
      response?.data,
      response?.data?.requestData,
      response?.responseList?.[0],
      response?.responseList?.[0]?.requestData,
      response?.responseList?.[0]?.data,
      response?.responseList?.[0]?.data?.[0]
    ];

    for (const container of containerCandidates) {
      if (!container || typeof container !== 'object') {
        continue;
      }

      for (const key of entityKeys) {
        const rows = container[key];
        if (Array.isArray(rows)) {
          return rows;
        }
      }
    }

    const responseItem = response?.responseList?.[0];
    if (responseItem && typeof responseItem === 'object') {
      const configuredEntityRows = responseItem[this.savedFilterEntity];
      if (Array.isArray(configuredEntityRows)) {
        return configuredEntityRows;
      }

      const fallbackEntityKey = Object.keys(responseItem).find((key) => Array.isArray(responseItem[key]));
      if (fallbackEntityKey) {
        return responseItem[fallbackEntityKey];
      }
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    return [];
  }

  private buildAppliedTopFilters() {
    return Object.keys(this.selectedTopFilters)
      .map((key) => {
        const value = this.normalizeTopFilterPayloadValue(key, this.selectedTopFilters[key]);
        return {
          field: key,
          operator: this.appliedTopFilterOperators[key] || '=',
          value
        };
      })
      .filter((filter: any) => {
        if (!filter?.field) {
          return false;
        }
        return this.hasMeaningfulFilterValue(filter.value);
      });
  }

  private hasMeaningfulFilterValue(value: any): boolean {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    const normalizedValue = String(value ?? '').trim();
    return normalizedValue !== '' && normalizedValue !== 'All';
  }

  private shouldSkipTopFilterPrefillForOperator(operator: any): boolean {
    const op = String(operator || '').trim().toLowerCase();
    return op === 'not_in'
      || op === 'not in'
      || op === 'is_not_null'
      || op === 'is not null'
      || op === 'is_null'
      || op === 'is null'
      || op === '!='
      || op === '<>'
      || op === 'not_equals'
      || op === 'not equals to'
      || op === 'is not equals to';
  }

  get allAppliedFilters(): Array<{ field: string; operator: string; value: any }> {
    const filters = this.buildAppliedFiltersForTopFilterPayload();
    return filters.filter(f => this.hasMeaningfulFilterValue(f.value));
  }

  private buildAppliedFiltersForTopFilterPayload() {
    const baseFilters: Array<{
      field: string;
      operator: string;
      value: any;
      logicalOperator?: '' | 'AND' | 'OR';
      beginScope?: string;
      endScope?: string;
    }> = [];

    this.deepDiveAppliedFilters.forEach((filter: {
      field: string;
      operator: string;
      value: string | string[];
      logicalOperator?: '' | 'AND' | 'OR';
      beginScope?: string;
      endScope?: string;
    }) => {
      const sourceField = String(filter?.field || '').trim();
      const field = this.resolveTopFilterKey(sourceField) || sourceField;
      const operator = String(filter?.operator || '=').trim() || '=';
      const forceArray = this.isListOperator(operator);
      const value = this.isPartNumberFilter({ key: field })
        ? this.normalizeManualFilterValues(filter?.value, true)
        : this.normalizeFilterPayloadValue(filter?.value, forceArray);
      if (!field) {
        return;
      }
      if (!this.hasMeaningfulFilterValue(value)) {
        return;
      }

      baseFilters.push({
        field,
        operator,
        value,
        logicalOperator: (() => {
          const op = String(filter?.logicalOperator || '').trim().toUpperCase();
          return op === 'AND' || op === 'OR' ? op : '';
        })(),
        beginScope: String(filter?.beginScope || ''),
        endScope: String(filter?.endScope || '')
      });
    });

    const appendedFilters: Array<{
      field: string;
      operator: string;
      value: any;
      logicalOperator?: '' | 'AND' | 'OR';
      beginScope?: string;
      endScope?: string;
    }> = [];

    Object.keys(this.selectedTopFilters || {}).forEach((field: string) => {
      // Append top dropdown filters only when user explicitly changed that filter.
      // Prefilled selections from existing appliedFilters should not create duplicate IN filters.
      if (!this.userModifiedTopFilters[field]) {
        return;
      }
      if (!field) {
        return;
      }
      const filterDef = this.topFilters.find((filter: any) => String(filter?.key || '').trim() === field);
      const isDropdownFilter = !!filterDef && !this.isPartNumberFilter(filterDef);
      const operator = isDropdownFilter ? 'in' : (this.appliedTopFilterOperators[field] || '=');
      const forceArray = isDropdownFilter || this.isListOperator(operator);
      const value = isDropdownFilter
        ? this.normalizeManualFilterValues(this.selectedTopFilters[field], false)
        : this.normalizeTopFilterPayloadValue(field, this.selectedTopFilters[field], forceArray);
      if (!this.hasMeaningfulFilterValue(value)) {
        return;
      }

      appendedFilters.push({
        field,
        operator,
        value,
        logicalOperator: '',
        beginScope: '',
        endScope: ''
      });
    });

    const mergedFilters = [...baseFilters, ...appendedFilters];
    if (appendedFilters.length > 0 && baseFilters.length > 0) {
      const tailBase = mergedFilters[baseFilters.length - 1];
      if (!tailBase.logicalOperator) {
        tailBase.logicalOperator = 'AND';
      }
    }
    if (appendedFilters.length > 1) {
      for (let i = baseFilters.length; i < mergedFilters.length - 1; i++) {
        mergedFilters[i].logicalOperator = 'AND';
      }
    }
    if (mergedFilters.length > 0) {
      mergedFilters[mergedFilters.length - 1].logicalOperator = '';
    }
    return mergedFilters;
  }

  private normalizeAppliedFiltersLikeRunSimulation(filters: any[]): Array<{
    field: string;
    operator: string;
    value: string | string[];
    logicalOperator: '' | 'AND' | 'OR';
    beginScope: string;
    endScope: string;
  }> {
    if (!Array.isArray(filters)) {
      return [];
    }

    const normalized = filters
      .map((filter: any) => {
        const field = String(filter?.field || filter?.attributeName || '').trim();
        if (!field) {
          return null;
        }

        const operator = String(filter?.operator || '=').trim() || '=';
        const forceArray = this.isListOperator(operator);
        const rawValue = Array.isArray(filter?.attributeValues) ? filter?.attributeValues : filter?.value;
        const value = this.normalizeFilterPayloadValue(rawValue, forceArray);
        const logicalRaw = String(filter?.logicalOperator || '').trim().toUpperCase();
        const logicalOperator: '' | 'AND' | 'OR' = logicalRaw === 'AND' || logicalRaw === 'OR' ? logicalRaw : '';

        return {
          field,
          operator,
          value,
          logicalOperator,
          beginScope: String(filter?.beginScope || ''),
          endScope: String(filter?.endScope || '')
        };
      })
      .filter((item): item is {
        field: string;
        operator: string;
        value: string | string[];
        logicalOperator: '' | 'AND' | 'OR';
        beginScope: string;
        endScope: string;
      } => item !== null);

    if (normalized.length > 0) {
      normalized[normalized.length - 1].logicalOperator = '';
    }
    return normalized;
  }

  private resolveAppliedFiltersForDeepDivePayload() {
    const latestDetailsResponse = this.simulationService.getDeepDiveDetailsResponse();
    const runScopedAppliedFilters = this.extractAppliedFiltersFromDetailsResponse(latestDetailsResponse);
    const componentAppliedFilters = this.buildAppliedFiltersForTopFilterPayload();
    const fallbackAppliedFilters = this.getFallbackAppliedFiltersForLoadPayload();
    const selectedAppliedFilters = componentAppliedFilters.length > 0
      ? componentAppliedFilters
      : (runScopedAppliedFilters.length > 0 ? runScopedAppliedFilters : fallbackAppliedFilters);
    const normalizedSelectedAppliedFilters = this.normalizeAppliedFiltersLikeRunSimulation(selectedAppliedFilters);

    console.log(this.APPLIED_FILTER_TRACE_KEY, 'deepDive.resolveAppliedFiltersForDeepDivePayload', {
      runScopedAppliedFilters,
      componentAppliedFilters,
      fallbackAppliedFilters,
      selectedAppliedFilters,
      normalizedSelectedAppliedFilters
    });

    return normalizedSelectedAppliedFilters;
  }

  private getFallbackAppliedFiltersForLoadPayload(): Array<{ field: string; operator: string; value: string | string[] }> {
    const detailsResponse = this.simulationService.getDeepDiveDetailsResponse();
    const parsedFallbackFilters = this.extractAppliedFiltersFromDetailsResponse(detailsResponse);

    return parsedFallbackFilters
      .map((filter: { field: string; operator: string; value: string | string[] }) => {
        const sourceField = String(filter?.field || '').trim();
        const field = this.resolveTopFilterKey(sourceField) || sourceField;
        const operator = String(filter?.operator || '=').trim() || '=';
        const forceArray = this.isListOperator(operator);
        const value = this.isPartNumberFilter({ key: field })
          ? this.normalizeManualFilterValues(filter?.value, true)
          : this.normalizeFilterPayloadValue(filter?.value, forceArray);

        return {
          field,
          operator,
          value
        };
      })
      .filter((filter: { field: string; value: string | string[] }) =>
        filter.field !== '' && this.hasMeaningfulFilterValue(filter.value)
      );
  }

  private getPriceChangesForRequestPayload(): Array<{ priceChangeValue: string; basedOn: string; priceChangeType: string }> {
    const detailsResponse = this.simulationService.getDeepDiveDetailsResponse();
    const candidates = [
      detailsResponse?.requestData?.priceChanges,
      detailsResponse?.responseList?.[0]?.requestData?.priceChanges,
      detailsResponse?.responseList?.[0]?.simulation?.[0]?.data?.priceChanges
    ];

    for (const candidate of candidates) {
      let parsedCandidate = candidate;

      if (typeof parsedCandidate === 'string') {
        const rawValue = parsedCandidate.trim();
        if (!rawValue) {
          continue;
        }

        try {
          parsedCandidate = JSON.parse(rawValue);
        } catch {
          continue;
        }
      }

      if (!Array.isArray(parsedCandidate) || parsedCandidate.length === 0) {
        continue;
      }

      const normalizedPriceChanges = parsedCandidate
        .map((item: any) => ({
          priceChangeValue: String(item?.priceChangeValue ?? '').trim(),
          basedOn: String(item?.basedOn ?? 'current_price').trim() || 'current_price',
          priceChangeType: String(item?.priceChangeType ?? 'percentage').trim() || 'percentage'
        }))
        .filter((item: { priceChangeValue: string }) => item.priceChangeValue !== '');

      if (normalizedPriceChanges.length > 0) {
        return normalizedPriceChanges;
      }
    }

    return [];
  }

  fetchTopFilterOptions(
    filterDef?: any,
    options: { preserveTriggerFilterOptions?: boolean } = {}
  ) {
    const triggerFilter = filterDef || this.topFilters.find(f => !this.isPartNumberFilter(f));
    if (!triggerFilter) {
      return;
    }

    const preserveTriggerFilterOptions = options.preserveTriggerFilterOptions === true;
    this.hasLoadedTopFilters = true;
    const topDropdownFilterName = this.resolveTopFilterRequestName();
    const appliedTopFilters = this.resolveAppliedFiltersForDeepDivePayload();
    console.log(this.TOP_FILTER_DEBUG_KEY, 'fetchTopFilterOptions.request', {
      triggerFilter: triggerFilter?.key,
      preserveTriggerFilterOptions,
      selectedTopFilters: { ...this.selectedTopFilters },
      appliedTopFilters
    });
    console.log(this.APPLIED_FILTER_TRACE_KEY , 'deepDive.fetchTopFilterOptions.appliedFilters hello3', {
      selectedAppliedTopFilters: appliedTopFilters
    });
    const payload = {
      entity: this.filterEntity,
      requestData: {
        entity: this.filterEntity,
        field: triggerFilter.key,
        filterName: topDropdownFilterName,
        ...(appliedTopFilters.length > 0 ? { appliedFilters: appliedTopFilters } : {}),
        ...this.getTopFilterRequestContext()
      },
      responseList: [],
      batchId: this.batchId,
      workflowId: this.workflowId,
      endpoints: [
        {
          functionName: "PasDeepDiveFilter",
          version: "1.0"
        }
      ]
    };

    const apiUrl = this.configService.getPlatformApiUrl();
    console.log(this.APPLIED_FILTER_TRACE_KEY, 'deepDive.fetchTopFilterOptions.payload.requestData.appliedFilters', payload?.requestData?.appliedFilters);
    console.log(`[DeepFilterDropDown] consolidated request using field ${triggerFilter.key}:`, JSON.stringify(payload.requestData));

    this.http.post<any>(apiUrl, payload).subscribe({
      next: (res) => {
        const responseItem = res?.responseList?.[0];
        if (!responseItem) {
          console.warn(`[DeepFilterDropDown] No responseList item found`);
          return;
        }

        console.log(`[DeepFilterDropDown] processing consolidated response keys:`, Object.keys(responseItem));

        this.topFilters.forEach(filter => {
          if (this.isPartNumberFilter(filter)) {
            return;
          }
          if (preserveTriggerFilterOptions && filter.key === triggerFilter.key) {
            return;
          }

          // 1. Try to find array for this specific filter key
          let arr = this.extractArrayFromResponse(responseItem, filter.key);

          // 2. Fallback for trigger filter only: preserve original Path 3/Fallback logic
          if (!arr && filter.key === triggerFilter.key) {
            arr = this.extractFallbackArray(responseItem, filter.key);
          }

          if (Array.isArray(arr)) {
            console.log(`[DeepFilterDropDown] populating options for ${filter.key} (${arr.length} items)`);
            this.populateFilterOptions(filter, arr);
          } else if (filter.key === triggerFilter.key) {
            console.warn(`[DeepFilterDropDown] No options found in response for trigger field ${filter.key}`);
          }
        });
        console.log(this.TOP_FILTER_DEBUG_KEY, 'fetchTopFilterOptions.responseMapped', {
          triggerFilter: triggerFilter?.key,
          selectedTopFiltersBeforePrefill: { ...this.selectedTopFilters },
          optionsSnapshot: this.topFilters.map((f: any) => ({
            key: f?.key,
            optionsCount: Array.isArray(f?.options) ? f.options.length : 0
          }))
        });

        if (this.prefillAppliedFiltersFromDetails) {
          this.applyPendingTopFilterPrefill();
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load consolidated top filter options', err);
      }
    });
  }

  private extractArrayFromResponse(responseItem: any, fieldKey: string): any[] | null {
    const containers = [
      responseItem?.dropdown?.[0],
      responseItem?.data?.[0],
      responseItem
    ];

    for (const container of containers) {
      if (!container || typeof container !== 'object') {
        continue;
      }

      // 1. Direct match: container[fieldKey]
      if (Array.isArray(container[fieldKey])) {
        return container[fieldKey];
      }

      // 2. Nested match: check inside properties that are arrays of objects
      // (e.g., res.responseList[0].distinct_dn_pricing[0].region)
      const keys = Object.keys(container);
      for (const key of keys) {
        const potentialNestedArray = container[key];
        if (Array.isArray(potentialNestedArray) && potentialNestedArray.length > 0) {
          const firstItem = potentialNestedArray[0];
          if (firstItem && typeof firstItem === 'object' && Array.isArray(firstItem[fieldKey])) {
            return firstItem[fieldKey];
          }
        }
      }
    }
    return null;
  }

  private extractFallbackArray(responseItem: any, fieldKey: string): any[] | null {
    // Path 3: direct array in data
    if (Array.isArray(responseItem?.data)) {
      return responseItem.data;
    }

    // Path 4/5 Fallback: any non-metadata array
    const metadataKeys = new Set(['operation', 'status', 'message', 'error', 'code']);
    const containers = [responseItem];
    for (const container of containers) {
      if (!container || typeof container !== 'object') {
        continue;
      }
      const key = Object.keys(container).find((k) => !metadataKeys.has(k) && Array.isArray(container[k]));
      if (key) {
        return container[key];
      }
    }
    return null;
  }

  private populateFilterOptions(filter: any, arr: any[]) {
    const apiOptions = arr.map(v => {
      // Handle both plain strings and objects with a value/label property
      const strVal = (typeof v === 'object' && v !== null)
        ? String(v[filter.key] ?? v.value ?? v.label ?? v.name ?? Object.values(v)[0] ?? '').trim()
        : String(v).trim();
      return strVal;
    }).filter((v: string) => v !== '');

    const selectedValues = this.normalizeManualFilterValues(this.selectedTopFilters[filter.key], false);
    const lockedValues = this.getResolvedLockedTopFilterValues(filter.key);
    filter.options = this.mergeTopFilterValues(
      Array.from(new Set(apiOptions)),
      [...selectedValues, ...lockedValues]
    );
    console.log(this.TOP_FILTER_DEBUG_KEY, 'populateFilterOptions', {
      filterKey: filter?.key,
      mappedOptionsCount: filter.options.length,
      mappedOptionsSample: filter.options.slice(0, 10)
    });
  }


  fetchAvailableColumns() {
    console.log("3/30/2026", this.selectedSimulationEntity);
    const columnType = this.selectedSimulationEntity === 'base_price' ? 'base_price' : 'dn_pricing';
    const payload = {
      entity: this.columnEntity,
      requestData: {
        entity: this.columnEntity,
        filterName: `${this.columnEntity}_filters`,
        type: columnType
      },
      responseList: [],
      batchId: this.batchId,
      workflowId: this.workflowId,
      endpoints: [
        {
          functionName: "DeepColumnDropDown",
          version: "1.0"
        }
      ]
    };

    const apiUrl = this.configService.getPlatformApiUrl();
    this.http.post<any>(apiUrl, payload).subscribe({
      next: (res) => {
        // As per backend: res.responseList[0].data[0].Columns
        const arr = res.responseList?.[0]?.data?.[0]?.Columns || res.responseList?.[0]?.data?.[0]?.columns;
        if (arr && Array.isArray(arr)) {
          // Map to label/value objects for the UI
          this.availableFields = arr.map((val: string) => ({
            label: val, value: val, key: val
          }));
          this.loaded = true;
        } else {
          console.error('Available columns API did not return expected array');
          this.loaded = true;
        }
      },
      error: (err) => {
        console.error('Failed to load available columns', err);
        this.loaded = true;
      }
    });
  }

  fetchSavedReports(reportToSelect?: any) {
    const apiUrl = this.configService.getPlatformApiUrl();
    const payload = {
      requestData: {
        entity: this.savedFilterEntity,
        created_user: this.resolveSavedFilterCreatedBy()
      },
      responseList: [],
      entity: this.savedFilterEntity,
      batchId: this.batchId,
      workflowId: this.workflowId,
      endpoints: [
        {
          functionName: this.savedFilterFunctionName,
          version: "1.0"
        }
      ],
      communicationType: "rest",
      version: "1.0"
    };

    console.log('[DeepDive] fetchSavedReports URL:', apiUrl, 'payload:', JSON.stringify(payload));

    this.http.post<any>(apiUrl, payload).subscribe({
      next: (res) => {
        console.log('[DeepDive] fetchSavedReports RAW response:', JSON.stringify(res, null, 2));

        const reports = this.extractSavedReportRows(res);
        this.latestSavedFilterRows = Array.isArray(reports) ? [...reports] : [];

        console.log('[DeepDive] fetchSavedReports parsed reports count:', reports.length);
        const apiReports = this.buildSavedReportsFromRows(reports);

        // Merge: keep locally-added reports that aren't in API results
        const apiNames = new Set(apiReports.map((r: any) => r.name));
        const localOnly = this.savedReports.filter((r: any) => !apiNames.has(r.name));
        this.savedReports = [...apiReports, ...localOnly];

        const candidateReport = reportToSelect || this.selectedReport;
        const candidateUuid = String(candidateReport?.uuid || '').trim();
        const candidateName = String(candidateReport?.name || '').trim();
        if (candidateUuid || candidateName) {
          const refreshedSelectedReport = this.savedReports.find((report: any) =>
            (candidateUuid && String(report?.uuid || '').trim() === candidateUuid)
            || (candidateName && String(report?.name || '').trim() === candidateName)
          );

          if (refreshedSelectedReport) {
            this.selectedReport = refreshedSelectedReport;
          }
        }
      },
      error: (err) => {
        console.error('Failed to fetch saved reports', err);
        // Keep existing local reports — don't clear the array
      }
    });
  }

  saveReport(reportName: string, existingFilterUuid: string | null = null) {
    const configEntity = this.savedFilterEntity || 'deep_dive_saved_filters';
    const createdBy = this.resolveSavedFilterCreatedBy();
    const selectedSimulationUuid = this.simulationUuid || this.simulationService.getLastRunUuid() || '';

    const payloadStrings = this.selectedColumns.map(col => typeof col === 'string' ? col : col.label);

    const payload = {
      requestData: {
        entity: configEntity,
        filter_name: reportName,
        created_user: createdBy,
        selected_columns: payloadStrings,
        ...(selectedSimulationUuid ? { simulation_uuid: selectedSimulationUuid } : {}),
        ...(existingFilterUuid ? { uuid: existingFilterUuid } : {})
      },
      responseList: [],
      entity: configEntity,
      batchId: this.batchId,
      workflowId: this.workflowId,
      endpoints: [
        {
          functionName: "DeepDiveSelectedColumn",
          version: "1.0"
        }
      ],
      communicationType: "rest",
      version: "1.0"
    };

    // Also persist to backend
    const apiUrl = this.configService.getPlatformApiUrl();
    console.log('[DeepDive] saveReport URL:', apiUrl);
    console.log('[DeepDive] saveReport payload:', JSON.stringify(payload, null, 2));
    this.http.post<any>(apiUrl, payload).subscribe({
      next: (res) => {
        console.log('[DeepDive] saveReport FULL response:', JSON.stringify(res, null, 2));
        console.log('[DeepDive] saveReport commitStatus:', res?.commitStatus, 'error:', res?.error);
        console.log('[DeepDive] saveReport responseList:', JSON.stringify(res?.responseList, null, 2));

        const normalizedExistingUuid = String(existingFilterUuid || '').trim();
        const existingIndex = this.savedReports.findIndex((report: any) =>
          (normalizedExistingUuid && String(report?.uuid || '').trim() === normalizedExistingUuid)
          || String(report?.name || '').trim() === reportName
        );

        const updatedReport = {
          ...(existingIndex >= 0 ? this.savedReports[existingIndex] : {}),
          id: normalizedExistingUuid || (existingIndex >= 0 ? this.savedReports[existingIndex].id : Date.now()),
          uuid: normalizedExistingUuid || (existingIndex >= 0 ? this.savedReports[existingIndex].uuid : ''),
          simulation_uuid: selectedSimulationUuid,
          name: reportName,
          columns: payloadStrings
        };

        if (existingIndex >= 0) {
          this.savedReports[existingIndex] = updatedReport;
        } else {
          this.savedReports.push(updatedReport);
        }
        this.savedReports = [...this.savedReports];
        this.selectedReport = updatedReport;
        this.switchTab('saved-selection');
        this.isEditingExistingReport = false;
      },
      error: (err) => {
        console.error('[DeepDive] Failed to save report to backend', err);
      }
    });
  }


  // --- UI Logic ---

  private getSimulationNameFromRoute(): string {
    return String(
      this.route.snapshot.queryParams?.['name']
      || this.route.snapshot.queryParams?.['simulation_name']
      || ''
    ).trim();
  }

  private getSimulationUuidFromRoute(): string {
    const directUuid = String(
      this.route.snapshot.queryParams?.['quote_uuid']
      || this.route.snapshot.queryParams?.['uuid']
      || this.route.snapshot.queryParams?.['simulation_uuid']
      || this.route.snapshot.queryParams?.['sim_deep_id']
      || ''
    ).trim();

    if (directUuid) {
      return directUuid;
    }

    const businessId = String(
      this.route.snapshot.queryParams?.['businessId']
      || this.route.snapshot.queryParams?.['business_id']
      || ''
    ).trim();

    return this.extractUuidFromBusinessId(businessId);
  }

  private extractUuidFromBusinessId(businessId: string): string {
    if (!businessId) return '';
    const match = businessId.match(/^([0-9A-Fa-f]{32})/);
    if (!match) {
      return '';
    }

    const hex = match[1];
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`.toUpperCase();
  }

  private getLoggedInUserEmail(): string {
    const keycloakInstance = this.keycloakService.getKeycloakInstance();
    const tokenParsed: any = keycloakInstance?.tokenParsed || {};
    const candidates = [
      tokenParsed?.email,
      tokenParsed?.preferred_username,
      tokenParsed?.upn
    ];

    const emailCandidate = candidates.find((value: any) => String(value ?? '').trim().includes('@'));
    const fallbackCandidate = candidates.find((value: any) => String(value ?? '').trim() !== '');
    return String(emailCandidate ?? fallbackCandidate ?? '').trim();
  }

  private getLoggedInUserName(): string {
    const keycloakInstance = this.keycloakService.getKeycloakInstance();
    const tokenParsed: any = keycloakInstance?.tokenParsed || {};
    const firstAndLastName = `${tokenParsed?.given_name ?? ''} ${tokenParsed?.family_name ?? ''}`.trim();
    const candidates = [
      tokenParsed?.name,
      firstAndLastName,
      tokenParsed?.preferred_username,
      tokenParsed?.email,
      tokenParsed?.upn
    ];

    const userNameCandidate = candidates.find((value: any) => String(value ?? '').trim() !== '');
    return String(userNameCandidate ?? '').trim();
  }

  scrollColumns(direction: number) {
    const scrollAmount = 100; // pixels to scroll
    window.scrollBy(0, direction * scrollAmount);
  }

  switchTab(tab: 'column-selection' | 'saved-selection' | 'report-view') {
    this.activeTab = tab;
    if (tab !== 'column-selection') {
      this.isColumnEditMode = false;
    }
    if (tab === 'saved-selection') {
      this.fetchSavedReports();
    }
    if (tab !== 'report-view') {
      this.currentPage = 1;
      this.headerColumnWidths = [];
      this.reportTableScrollLeft = 0;
      this.reportTableContentWidth = 0;
    } else {
      this.queueGrandTotalLayoutSync();
    }
  }

  addColumn() {
    const selectedLabel = this.getSelectedColumnLabel(this.selectedFieldToAdd);
    if (selectedLabel && !this.selectedColumns.find(c => this.getSelectedColumnLabel(c) === selectedLabel)) {
      this.selectedColumns.push(this.selectedFieldToAdd);
    }
    this.selectedFieldToAdd = null;
  }

  getSelectedColumnLabel(column: any): string {
    return String(column?.label ?? column ?? '').trim();
  }

  toggleColumnEditMode() {
    if (this.selectedColumns.length === 0) {
      this.isColumnEditMode = false;
      return;
    }
    this.isColumnEditMode = !this.isColumnEditMode;
  }

  removeSelectedColumn(index: number) {
    if (index < 0 || index >= this.selectedColumns.length) {
      return;
    }

    this.selectedColumns.splice(index, 1);
    this.selectedColumns = [...this.selectedColumns];

    if (this.selectedColumns.length === 0) {
      this.isColumnEditMode = false;
    }
  }

  onFieldDropdownOpenedChange(isOpened: boolean) {
    if (isOpened) {
      setTimeout(() => {
        const searchInput = document.querySelector('.mat-mdc-select-panel .field-search-input') as HTMLInputElement | null;
        searchInput?.focus();
        searchInput?.select();
      }, 0);
      return;
    }

    if (!isOpened) {
      this.fieldSearchText = '';
    }
  }

  onFieldSearchChange(value: string) {
    this.fieldSearchText = String(value || '');
  }

  getFilteredAvailableFields(): any[] {
    const searchTerm = this.fieldSearchText.trim().toLowerCase();
    if (!searchTerm) {
      return this.availableFields;
    }

    return this.availableFields.filter((field: any) => {
      const label = String(field?.label || '').toLowerCase();
      const key = String(field?.key || field?.value || '').toLowerCase();
      return label.includes(searchTerm) || key.includes(searchTerm);
    });
  }

  getSelectedColumnsText(): string {
    if (!this.selectedColumns || this.selectedColumns.length === 0) {
      return 'No columns selected yet.';
    }
    // E.g. "Region, Business Owner, Change Reason & Impact %"
    const labels = this.selectedColumns.map(c => c.label || c);
    if (labels.length === 1) return labels[0];

    const last = labels.pop();
    return labels.join(', ') + ' & ' + last;
  }

  openSaveAsDialog() {
    if (this.isEditingExistingReport && this.selectedReport?.name) {
      const existingFilterUuid = String(this.selectedReport?.uuid || '').trim() || null;
      this.saveReport(this.selectedReport.name, existingFilterUuid);
      return;
    }

    const dialogRef = this.dialog.open(DeepDiveSaveDialogComponent, {
      width: '400px',
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.saveReport(result);
      }
    });
  }

  selectReport(report: any) {
    this.selectedReport = report;
    this.isEditingExistingReport = false;
    this.fetchSavedReports(report);
  }

  // Default columns always shown on the RIGHT side of the report
  private defaultReportColumns = [
    { key: 'current_sales', originalKey: 'Current Sales', label: 'Current Sales', isNumeric: true },
    { key: 'dollar_impact', originalKey: 'Dollar Impact', label: 'Total Impact', isNumeric: true },
    { key: 'percentage_impact', originalKey: 'Impact %', label: 'Total Impact %', isNumeric: true }
  ];

  loadReport(selectedColumnsOverride?: string[]) {
    if (this.isReportLoadInProgress) return;
    if (!this.selectedReport && (!Array.isArray(selectedColumnsOverride) || selectedColumnsOverride.length === 0)) return;
    this.isReportLoadInProgress = true;
    this.hasHierarchicalGrouping = false;

    const selectedReportColumns = (Array.isArray(selectedColumnsOverride) && selectedColumnsOverride.length > 0)
      ? selectedColumnsOverride
      : this.getSavedColumnsFromRowData(this.selectedReport);
    this.lastLoadedDeepDiveColumns = [...selectedReportColumns];

    // Create column definitions from user-selected columns (LEFT side)
    const defaultKeys = new Set(this.defaultReportColumns.map((c: any) => this.getCanonicalReportKey(c.key)));
    const userColumns = selectedReportColumns
      .filter((colName: string) => !defaultKeys.has(this.getCanonicalReportKey(colName)))
      .map((colName: string) => {
        const snakeKey = this.toApiColumnKey(colName);
        return {
          key: snakeKey,
          originalKey: colName,
          label: colName,
          isNumeric: colName.toLowerCase().includes('sales') ||
            colName.toLowerCase().includes('volume') ||
            colName.toLowerCase().includes('impact') ||
            colName.toLowerCase().includes('dollar') ||
            colName.toLowerCase().includes('percentage') ||
            colName.includes('$') || colName.includes('%')
        };
      });

    // Merge: user-selected columns (left) + default columns (right)
    this.activeReportColumns = [...userColumns, ...this.defaultReportColumns];
    this.totalRecords = 0;
    this.fetchReportPage(1, userColumns, defaultKeys, selectedReportColumns);
  }

  private fetchReportPage(
    page: number,
    userColumns?: any[],
    defaultKeys?: Set<string>,
    selectedReportColumns?: string[]
  ): void {
    const selectedColumns = (Array.isArray(selectedReportColumns) && selectedReportColumns.length > 0)
      ? selectedReportColumns
      : this.selectedReport
        ? this.getSavedColumnsFromRowData(this.selectedReport)
        : this.lastLoadedDeepDiveColumns;
    if (selectedColumns.length === 0) {
      this.isReportLoadInProgress = false;
      return;
    }
    const fallbackDefaultKeys = new Set(this.defaultReportColumns.map((c: any) => this.getCanonicalReportKey(c.key)));
    const resolvedDefaultKeys = defaultKeys || fallbackDefaultKeys;
    const resolvedUserColumns = userColumns || selectedColumns
      .filter((colName: string) => !resolvedDefaultKeys.has(this.getCanonicalReportKey(colName)))
      .map((colName: string) => {
        const snakeKey = this.toApiColumnKey(colName);
        return {
          key: snakeKey,
          originalKey: colName,
          label: colName,
          isNumeric: colName.toLowerCase().includes('sales') ||
            colName.toLowerCase().includes('volume') ||
            colName.toLowerCase().includes('impact') ||
            colName.toLowerCase().includes('dollar') ||
            colName.toLowerCase().includes('percentage') ||
            colName.includes('$') || colName.includes('%')
        };
      });

    const safePage = Math.max(1, page);
    const resolvedAppliedTopFilters = this.resolveAppliedFiltersForDeepDivePayload();
    const priceChanges = this.getPriceChangesForRequestPayload();
    const selectedSimulationUuid = String(this.simulationUuid || this.simulationService.getLastRunUuid() || '').trim();
    const requestEntity = this.resolveLoadRequestEntity();
    const deepDiveSelectedColumns = selectedColumns
      .map((colName: string) => this.toApiColumnKey(colName))
      .filter((colName: string) => colName !== '');
    const deepDiveSavedFilters = this.buildSavedFiltersForLoadPayload();
    const contextPayload = this.buildLoadContextPayload(this.selectedSimulationEntity || requestEntity);
    const resolvedContextType = contextPayload?.contextType || 'region';
    const offset = Math.max(0, Number(this.loadOffset) || 0) + ((safePage - 1) * this.pageSize);

    const payload = {
      entity: this.loadPayloadEntity,
      requestData: {
        entity: requestEntity,
        contextType: resolvedContextType,
        ...(selectedSimulationUuid ? { uuid: selectedSimulationUuid } : {}),
        ...(contextPayload || {}),
        limit: this.pageSize,
        offset,
        appliedFilters: resolvedAppliedTopFilters,
        priceChanges,
        deep_dive_selected_columns: deepDiveSelectedColumns,
        deep_dive_saved_filters: deepDiveSavedFilters
      },
      responseList: [],
      batchId: this.batchId,
      workflowId: this.workflowId,
      endpoints: [
        {
          functionName: this.loadFunctionName,
          version: "1.0"
        }
      ]
    };

    const apiUrl = this.configService.getPlatformApiUrl();
    console.log(this.APPLIED_FILTER_TRACE_KEY, 'deepDive.fetchReportPage.payload.requestData.appliedFilters', payload?.requestData?.appliedFilters);
    console.log('[DeepDive] loadReport payload:', JSON.stringify(payload, null, 2));
    this.http.post<any>(apiUrl, payload).subscribe({
      next: (res) => this.handleLoadReportResponse(res, safePage, resolvedUserColumns, resolvedDefaultKeys),
      error: (err) => {
        console.error('[DeepDive] Failed to load report data', err);
        this.handleLoadReportError(safePage);
      }
    });
  }

  private handleLoadReportError(safePage: number): void {
    this.isReportLoadInProgress = false;
    if (safePage === 1) {
      this.hasHierarchicalGrouping = false;
      this.reportData = [];
      this.summaryData = null;
      this.totalRecords = 0;
      this.currentPage = 1;
      this.switchTab('report-view');
      this.queueGrandTotalLayoutSync();
    }
  }

  private handleLoadReportResponse(
    res: any,
    safePage: number,
    resolvedUserColumns: any[],
    resolvedDefaultKeys: Set<string>
  ): void {
    console.log('[DeepDive] loadReport raw response:', JSON.stringify(res, null, 2));
    const responseData = res?.responseList?.[0];
    console.log('[DeepDive] responseData keys:', responseData ? Object.keys(responseData) : 'null');

    const responseContainers = [
      responseData?.simulation?.[0]?.data,
      responseData?.requestData,
      res?.requestData,
      responseData,
      res
    ].filter((container: any) => container && typeof container === 'object');

    const parseArrayLike = (value: any): any[] => {
      if (Array.isArray(value)) {
        return value;
      }

      const raw = String(value ?? '').trim();
      if (!raw) {
        return [];
      }

      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };

    const getFirstArrayByKey = (keys: string[]): any[] => {
      for (const key of keys) {
        for (const container of responseContainers) {
          const parsedArray = parseArrayLike(container?.[key]);
          if (parsedArray.length > 0) {
            return parsedArray;
          }
        }
      }
      return [];
    };

    const viewList = getFirstArrayByKey(['deep_dive_view_pas', 'view_pas_all_region', 'view_pas_oem_region', this.entityName]);
    const apiRows = viewList.map((item: any) => item.data || item);
    const normalizedReportResult = this.normalizeReportRows(apiRows);
    const normalizedRows = normalizedReportResult.rows;
    console.log('[DeepDive] viewList:', viewList, 'reportData:', normalizedRows);

    const userColumnKeys = new Set(resolvedUserColumns.map((col: any) => this.getCanonicalReportKey(col.key)));
    const responseKeys: string[] = [];
    normalizedRows.forEach((row: any) => {
      Object.keys(row || {}).forEach((key: string) => {
        if (!key.startsWith('__') && !responseKeys.includes(key)) {
          responseKeys.push(key);
        }
      });
    });

    const additionalResponseColumns = responseKeys
      .filter((key: string) => {
        const canonicalKey = this.getCanonicalReportKey(key);
        return !resolvedDefaultKeys.has(canonicalKey) && !userColumnKeys.has(canonicalKey);
      })
      .map((key: string) => ({
        key,
        originalKey: key,
        label: key.split('_').map((part: string) => part ? part.charAt(0).toUpperCase() + part.slice(1) : '').join(' '),
        isNumeric: key.includes('sales')
          || key.includes('volume')
          || key.includes('impact')
          || key.includes('dollar')
          || key.includes('percentage')
      }));

    this.activeReportColumns = [...resolvedUserColumns, ...additionalResponseColumns, ...this.defaultReportColumns];
    const groupingColumns = this.activeReportColumns
      .filter((col: any) => !col?.isNumeric)
      .map((col: any) => this.getCanonicalReportKey(col?.key))
      .filter((key: string, index: number, arr: string[]) => key !== '' && arr.indexOf(key) === index);
    const groupedRows = this.addGroupingMetadata(normalizedRows, groupingColumns);
    this.hasHierarchicalGrouping = normalizedReportResult.hasHierarchy && groupingColumns.length > 0;

    const summaryArr = getFirstArrayByKey(['grand_total', 'deep_dive_summary', 'summary']);
    const apiSummary = summaryArr.length > 0 ? (summaryArr[0].data || summaryArr[0]) : null;
    this.reportData = groupedRows;
    this.totalRecords = this.getTotalRecordsFromResponse(responseContainers, this.reportData.length, safePage);
    this.summaryData = apiSummary;

    console.log('[DeepDive] summaryData:', this.summaryData);

    this.currentPage = safePage;
    this.isReportLoadInProgress = false;
    this.switchTab('report-view');
    this.cdr.detectChanges();
    this.queueGrandTotalLayoutSync();
  }

  // Navigate back to simulation list / previous Deep Dive tab
  goBack() {
    // From report view, go back to saved-selection (reverse of load)
    if (this.activeTab === 'report-view') {
      this.switchTab('saved-selection');
      return;
    }

    // From saved-selection, go back to column-selection
    if (this.activeTab === 'saved-selection') {
      this.switchTab('column-selection');
      return;
    }

    // From column-selection, prefer navigating back to Simulation Details (preserve uuid/name),
    // otherwise fall back to the overview list
    if (this.activeTab === 'column-selection') {
      const uuid = String(this.simulationUuid || this.getSimulationUuidFromRoute() || this.simulationService.getLastRunUuid() || '').trim();
      const name = String(this.simulationName || this.getSimulationNameFromRoute() || '').trim();

      const hasParams = uuid !== '' || name !== '';
      if (hasParams) {
        const queryParams: any = {};
        if (uuid) queryParams.uuid = uuid;
        if (name) queryParams.name = name;
        this.router.navigate(['/price-file-simulation', 'detail'], { queryParams });
        return;
      }

      this.router.navigate(['/price-file-simulation']);
      return;
    }

    // If this deep-dive was opened from Simulation Details with loadAllTopFiltersOnInit=true,
    // Back should open saved-selection instead of navigating away.
    const openedFromDetailsUuid = String(this.route.snapshot.queryParams?.['uuid'] || '').trim();
    const openedLoadAll = this.parseBooleanQueryParam(this.route.snapshot.queryParams?.['loadAllTopFiltersOnInit']);
    if (openedFromDetailsUuid && openedLoadAll === true) {
      this.switchTab('saved-selection');
      return;
    }

    // Otherwise navigate back to the simulation detail page (preserve uuid/name if available)
    const uuid = String(this.simulationUuid || this.simulationService.getLastRunUuid() || '').trim();
    const name = String(this.simulationName || '').trim();

    const hasParams = uuid !== '' || name !== '';
    if (hasParams) {
      const queryParams: any = {};
      if (uuid) queryParams.uuid = uuid;
      if (name) queryParams.name = name;
      this.router.navigate(['/price-file-simulation', 'detail'], { queryParams });
      return;
    }

    // Fallback to browser history
    try {
      window.history.back();
    } catch (e) {
      this.router.navigate(['/price-file-simulation', 'detail']);
    }
  }

  // Reset all selections and filters
  resetAll() {
    this.selectedTopFilters = {};
    this.userModifiedTopFilters = {};
    this.pendingTopFilterPrefill = {};
    this.reapplyLockedTopFilterSelections();
    this.selectedColumns = [];
    this.isColumnEditMode = false;
    this.selectedFieldToAdd = null;
    this.selectedReport = null;
    this.isEditingExistingReport = false;
    this.hasHierarchicalGrouping = false;
    this.lastLoadedDeepDiveColumns = [];
    this.reportData = [];
    this.summaryData = null;
    this.activeReportColumns = [];
    this.totalRecords = 0;
    this.currentPage = 1;
    this.activeTab = 'column-selection';
    this.isReportLoadInProgress = false;
    this.isExportInProgress = false;
    this.headerColumnWidths = [];
    this.reportTableScrollLeft = 0;
    this.reportTableContentWidth = 0;
  }

  // Reset only the top filters and reload the report
  resetFiltersOnly() {
    this.selectedTopFilters = {};
    this.userModifiedTopFilters = {};
    this.pendingTopFilterPrefill = {};
    this.reapplyLockedTopFilterSelections();
    this.cdr.detectChanges();
    this.queueReportReloadOnTopFilterChange();
  }

  // Trigger export API and show success message
  exportReport(selectedReportColumns?: string[]) {
    if (this.isExportInProgress || !this.reportData || this.reportData.length === 0) return;
    this.isExportInProgress = true;

    const selectedColumns = (Array.isArray(selectedReportColumns) && selectedReportColumns.length > 0)
      ? selectedReportColumns
      : this.selectedReport
        ? this.getSavedColumnsFromRowData(this.selectedReport)
        : this.lastLoadedDeepDiveColumns;
    if (selectedColumns.length === 0) {
      this.isReportLoadInProgress = false;
      return;
    }
    const deepDiveSelectedColumns = selectedColumns
      .map((colName: string) => this.toApiColumnKey(colName))
      .filter((colName: string) => colName !== '');

    const exportPayloadEntity = this.resolveExportPayloadEntity();
    const exportRequestEntity = this.resolveExportRequestEntity();
    const colsKey = this.resolveExportColumnsKey();
    const rawCols = this.activeReportColumns.map((c: any) => c.key);
    const resolvedAppliedTopFilters = this.resolveAppliedFiltersForDeepDivePayload();
    const priceChanges = this.getPriceChangesForRequestPayload();
    const loggedInUserEmail = this.getLoggedInUserEmail();
    const loggedInUserName = this.getLoggedInUserName();

    const payload = {
      entity: exportPayloadEntity,
      requestData: {
        entity: exportRequestEntity,
        appliedFilters: resolvedAppliedTopFilters,
        priceChanges,
        ...(loggedInUserEmail ? { emailTo: loggedInUserEmail } : {}),
        ...(loggedInUserName ? { userName: loggedInUserName } : {}),
        [colsKey]: rawCols,
        deep_dive_selected_columns: deepDiveSelectedColumns
      },
      responseList: [],
      batchId: this.batchId,
      workflowId: this.workflowId,
      endpoints: [
        {
          functionName: "PasOutbound",
          version: "1.0"
        }
      ]
    };

    const apiUrl = this.configService.getPlatformApiUrl();
    console.log(this.APPLIED_FILTER_TRACE_KEY, 'deepDive.exportReport.payload.requestData.appliedFilters', payload?.requestData?.appliedFilters);
    console.log('[DeepDive] exportReport payload:', JSON.stringify(payload, null, 2));
    this.showExportingPopup();
    this.http.post<any>(apiUrl, payload).subscribe({
      next: () => {
        this.isExportInProgress = false;
      },
      error: (err) => {
        this.isExportInProgress = false;
        console.error('[DeepDive] exportReport failed', err);
      }
    });
  }

  private showExportingPopup(): void {
    const configuredPopupMessage = String(this.data?.exportInProgressMessage || '').trim();
    const configuredSuccessMessage = String(this.exportSuccessMessage || '').trim();
    const fallbackMessage = 'Exporting...';
    this.exportPopupMessage = configuredPopupMessage || configuredSuccessMessage || fallbackMessage;
    this.exportPopupVisible = true;
  }

  closeExportPopupAndNavigateToDeepDive(): void {
    this.exportPopupVisible = false;
    this.returnToDeepDiveLanding();
  }

  // Edit a saved report - load its columns into column-selection tab
  editReport() {
    if (!this.selectedReport) return;
    this.isEditingExistingReport = true;
    this.isColumnEditMode = false;
    this.selectedColumns = this.selectedReport.columns.map((colName: string) => {
      const found = this.availableFields.find((f: any) => f.label === colName);
      return found || { label: colName, value: colName, key: colName };
    });
    this.switchTab('column-selection');
  }

  private getMappedSummaryValue(colKey: string): any {
    if (!this.summaryData) return '';

    const canonicalKey = this.getCanonicalReportKey(colKey);
    if (!this.isGrandTotalMetricKey(canonicalKey)) {
      return '';
    }

    const mappedSummaryKeys: Record<string, string[]> = {
      current_sales: ['totalCurrentSales', 'totalCurrentSalesValue', 'current_sales'],
      current_future_sales: ['totalCurrentFutureSales', 'totalFutureSalesValue', 'current_future_sales'],
      current_impact: ['totalCurrentImpact', 'current_impact'],
      dollar_impact: ['totalDollarImpact', 'total_impact', 'dollar_impact'],
      percentage_impact: ['totalPercentageImpact', 'totalImpactResult', 'totalOverallImpact', 'totaloverallImpact', 'impact_percent'],
      'impact_%': ['totalImpactResult', 'totalOverallImpact', 'totaloverallImpact', 'impact_percent']
    };

    const candidateKeys = mappedSummaryKeys[canonicalKey] || [];

    for (const key of candidateKeys) {
      const val = this.summaryData[key];
      if (val !== undefined && val !== null && String(val) !== '') {
        return val;
      }
    }

    return '';
  }

  private parseNumericForSummary(value: any): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    const raw = String(value ?? '').trim();
    if (!raw) {
      return 0;
    }

    const sanitized = raw.replace(/,/g, '').replace(/[^0-9.-]/g, '');
    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private isGrandTotalMetricKey(canonicalKey: string): boolean {
    return canonicalKey === 'current_sales'
      || canonicalKey === 'current_future_sales'
      || canonicalKey === 'current_impact'
      || canonicalKey === 'dollar_impact'
      || canonicalKey === 'percentage_impact';
  }

  private getFallbackSummaryValue(colKey: string): any {
    const rows = Array.isArray(this.reportData) ? this.reportData : [];
    if (rows.length === 0) {
      return '';
    }

    const canonicalKey = this.getCanonicalReportKey(colKey);
    if (!this.isGrandTotalMetricKey(canonicalKey)) {
      return '';
    }
    if (canonicalKey === 'percentage_impact') {
      const totalSales = rows.reduce((sum: number, row: any) =>
        sum + this.parseNumericForSummary(this.getReportValueByColumnKey(row, 'current_sales')), 0);
      const totalDollar = rows.reduce((sum: number, row: any) =>
        sum + this.parseNumericForSummary(this.getReportValueByColumnKey(row, 'dollar_impact')), 0);
      return totalSales === 0 ? 0 : (totalDollar / totalSales) * 100;
    }

    return rows.reduce((sum: number, row: any) =>
      sum + this.parseNumericForSummary(this.getReportValueByColumnKey(row, canonicalKey)), 0);
  }

  get hasSummaryRow(): boolean {
    return !!this.summaryData || (Array.isArray(this.reportData) && this.reportData.length > 0);
  }

  private formatNumericForUi(value: any, context: string = ''): string {
    return formatDisplayValueByContext(value, context);
  }

  getReportCellValue(row: any, col: any): string {
    const rawValue = this.getReportValueByColumnKey(row, col?.key);
    if (!col?.isNumeric) {
      return rawValue === null || rawValue === undefined ? '' : String(rawValue);
    }

    return this.formatNumericForUi(rawValue, `${col?.key || ''} ${col?.label || ''}`);
  }

  shouldRenderReportCell(row: any, col: any): boolean {
    if (!this.hasHierarchicalGrouping) {
      return true;
    }

    const groupingKey = this.getGroupingKeyForColumn(col?.key);
    if (!groupingKey) {
      return true;
    }

    return row?.__groupMeta?.showCell?.[groupingKey] !== false;
  }

  getReportCellRowSpan(row: any, col: any): number | null {
    if (!this.hasHierarchicalGrouping) {
      return null;
    }

    const groupingKey = this.getGroupingKeyForColumn(col?.key);
    if (!groupingKey) {
      return null;
    }

    const span = Number(row?.__groupMeta?.rowSpan?.[groupingKey] ?? 1);
    return span > 1 ? span : null;
  }

  // Get summary value for a column key
  getSummaryValue(colKey: string, colIndex: number): string {
    const canonicalKey = this.getCanonicalReportKey(colKey);
    const mappedVal = this.getMappedSummaryValue(colKey);
    const fallbackVal = this.getFallbackSummaryValue(colKey);
    const val = mappedVal !== '' ? mappedVal : fallbackVal;
    const firstCol = this.activeReportColumns[0];
    const showLabelOnlyInFirstCol = this.activeReportColumns.length > 1 && !firstCol?.isNumeric;

    // Show "Grand Total" label only when first column is dimensional (non-numeric).
    if (colIndex === 0 && showLabelOnlyInFirstCol) return 'Grand Total';

    // If there is only one non-numeric column, keep old label+value behavior.
    if (this.activeReportColumns.length === 1 && !firstCol?.isNumeric) return 'Grand Total: ' + val;

    if (this.activeReportColumns[colIndex]?.isNumeric) {
      if (!this.isGrandTotalMetricKey(canonicalKey)) {
        return '';
      }
      return this.formatNumericForUi(val, `${colKey || ''}`);
    }

    return '';
  }

  get totalPages(): number {
    if (!this.totalRecords || this.totalRecords <= 0) {
      return 1;
    }
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  get pagedReportData(): any[] {
    return this.reportData || [];
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.fetchReportPage(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.fetchReportPage(this.currentPage + 1);
    }
  }

  private returnToDeepDiveLanding(): void {
    this.switchTab(this.exportSuccessTab);

    if (this.exportSuccessTab !== 'column-selection') {
      return;
    }

    // Keep selected top filters, but reset report/selection state
    // so the user lands on the Deep Dive column-selection screen.
    this.selectedColumns = [];
    this.isColumnEditMode = false;
    this.selectedFieldToAdd = null;
    this.selectedReport = null;
    this.isEditingExistingReport = false;
    this.hasHierarchicalGrouping = false;
    this.lastLoadedDeepDiveColumns = [];
    this.reportData = [];
    this.summaryData = null;
    this.activeReportColumns = [];
    this.totalRecords = 0;
    this.currentPage = 1;
    this.headerColumnWidths = [];
    this.reportTableScrollLeft = 0;
    this.reportTableContentWidth = 0;
  }

  private getTotalRecordsFromResponse(responseContainers: any[], fallback: number, page: number): number {
    const totalCountKeys = ['totalCount', 'total_count', 'totalRecords', 'total_records', 'recordCount', 'count'];
    for (const key of totalCountKeys) {
      for (const container of responseContainers) {
        const rawValue = container?.[key];
        const parsed = Number(rawValue);
        if (!Number.isNaN(parsed) && parsed >= 0) {
          return parsed;
        }
      }
    }

    const computedFromPage = ((page - 1) * this.pageSize) + Math.max(0, fallback);
    return computedFromPage;
  }

}
